import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { IBreadcrumbs, BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { IonContent, IonIcon, IonSpinner } from "@ionic/angular/standalone";
import { RouterModule } from '@angular/router';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ScaleType } from '@swimlane/ngx-charts';
import { addIcons } from 'ionicons';
import { wallet, wifi, pin, pinOutline, options, statsChart, alertOutline, optionsOutline, downloadOutline, checkmark, fileTrayOutline, textOutline, eyeOutline, filter, closeCircleOutline, closeOutline, globeOutline, settingsOutline, eyeOffOutline, timeOutline, timerOutline } from 'ionicons/icons';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { debounceTime, forkJoin, map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatMenuModule } from "@angular/material/menu";
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../../shared/enums/ionicColors.enum';
import { UserSettingsService } from '../../../core/services/user-settings.service';
import {
  AnalyticsContext,
  AnalyticsEventComparisonLevel,
  AnalyticsHourlyHeatmapStatus,
  AnalyticsPeriod,
  DashboardWidget,
  IAnalyticsEventDetail,
  IAnalyticsEventComparisonOptionsResponse,
  IAnalyticsEventDetailsResponse,
  IAnalyticsEventsSummary,
  IAnalyticsEventsTrendResponse,
  IAnalyticsHourlyHeatmapResponse,
  IAnalyticsManagedEventsTrendResponse,
  IAnalyticsOperationsSummary,
  IAnalyticsResponseTimeTrendResponse,
  IUsageAnalyticsDailyResponse,
  IUsageAnalyticsHeatmapResponse,
  IUsageAnalyticsSummary,
  IUsageAnalyticsUsersResponse,
  UsageAnalyticsKind,
  WidgetState
} from '../../../shared/models/analytics';
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MaterialModule } from "../../../shared/modules/material.module";
import { IAnalyticsAlertTypes, IAnalyticsOnlineSensors } from '../../../shared/models/be/analytics';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart, GaugeChart, LineChart, PieChart, SunburstChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsOption } from 'echarts/types/dist/shared';
import { GuideService } from '../../../core/services/guide.service';
import { AnalyticsTutorialComponent } from '../../../shared/components/analytics-tutorial/analytics-tutorial.component';
import { CustomerService } from '../../../core/services/customer.service';
import { FacilitiesService } from '../../../core/services/facilities.service';
import { IUser } from '../../../shared/models/be/user';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { PostureStatisticsComponent } from '../../../shared/components/posture-statistics/posture-statistics.component';
import { SensorConnectivityComponent } from '../../../shared/components/sensor-connectivity/sensor-connectivity.component';
echarts.use([PieChart, SunburstChart, BarChart, LineChart, GaugeChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer]);

export type AnalyticsKpiDetailType = 'events' | 'falls' | 'responseTime';
export type AnalyticsKpiComparisonLevel = AnalyticsEventComparisonLevel;
export type AnalyticsKpiComparisonMode = 'absolute' | 'variation';

interface AnalyticsKpiComparisonOption {
  id: string;
  label: string;
  level: AnalyticsKpiComparisonLevel;
  hierarchyPath?: string;
}

interface AnalyticsKpiDetailTrendPoint {
  label: string;
  value: number;
}

interface AnalyticsKpiDetailMock {
  details: IAnalyticsEventDetailsResponse;
  points: AnalyticsKpiDetailTrendPoint[];
}

export type AnalyticsDashboardCardId =
  | 'kpi_sensors'
  | 'kpi_events'
  | 'kpi_falls'
  | 'kpi_response_time'
  | 'event_types'
  | 'managed_events_gauge'
  | 'hourly_heatmap'
  | 'usage_adoption'
  | 'usage_daily'
  | 'posture_statistics';

export interface AnalyticsDashboardCardPreference {
  id: AnalyticsDashboardCardId;
  label: string;
  visible: boolean;
  superUserOnly?: boolean;
}

@Component({
  selector: 'app-settings',
  templateUrl: './analytics.page.html',
  styleUrls: ['./analytics.page.scss'],
  imports: [
    AnalyticsTutorialComponent,
    IonContent,
    CommonModule,
    TranslateModule,
    RouterModule,
    MatExpansionModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    IonIcon,
    MatButtonToggleModule,
    IonSpinner,
    MatMenuModule,
    MatProgressBarModule,
    MaterialModule,
    BreadcrumbsComponent,
    NgxEchartsDirective,
    PostureStatisticsComponent,
    SensorConnectivityComponent
  ],
  providers: [
    provideEchartsCore({ echarts }),
  ]
})
export class AnalyticsPage implements OnInit, OnDestroy {
  public readonly analyticsTutorialMode: boolean;
  public breadcrumbsList: IBreadcrumbs[] = [{
    icon: 'query_stats',
    label: 'ANALYTICS'
  }];

  public isDataLoading: boolean = false;

  public widgetRegistry: DashboardWidget[] = [];

  // Auto-Save & Destroy
  private saveConfigSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  // --- GESTIONE CONTESTO (FILTRI) ---
  public currentContext: AnalyticsContext = {
    customerId: undefined,
    organizationId: undefined,
    buildingId: undefined,
    unitId: undefined
  };
  public isContextLoading: boolean = false;

  // Liste per le Select (Da popolare tramite API)
  public customersList: any[] = [];
  public organizationsList: any[] = [];
  public buildingsList: any[] = [];
  public unitsList: any[] = [];
  public currentUser: IUser | null = null;

  constructor(
    private analyticsService: AnalyticsService,
    private toastService: ToastService,
    private userSettingsService: UserSettingsService,
    private customerService: CustomerService,
    private facilitiesService: FacilitiesService,
    private requestsService: RequestsService,
    public guide: GuideService = null!
  ) {
    this.analyticsTutorialMode = guide?.analyticsPracticeRequested() ?? false;
    /* Aggiungo le icone di Ionic */
    addIcons({
      'wifi': wifi, 'wallet': wallet, 'pin': pin, 'pin-outline': pinOutline,
      'options': options, 'stats-chart': statsChart, 'alert-outline': alertOutline,
      'settings-outline': settingsOutline, 'download-outline': downloadOutline,
      'checkmark': checkmark, 'file-tray-outline': fileTrayOutline,
      'text-outline': textOutline, 'eye-outline': eyeOutline,
      'filter': filter, 'close-circle-outline': closeCircleOutline,
      'close-outline': closeOutline,
      'globe-outline': globeOutline,
      'options-outline': optionsOutline,
      'eye-off-outline': eyeOffOutline,
      'timer-outline': timerOutline
    });
  }

  cardColor: string = '#232837';
  public newDashboardPeriod: AnalyticsPeriod = '24h';
  public readonly newDashboardPeriodOptions: ReadonlyArray<{
    value: AnalyticsPeriod;
    label: string;
    contextLabel: string;
  }> = [
      { value: '24h', label: 'Ultime 24 ore', contextLabel: 'ultime 24 ore' },
      { value: '7d', label: 'Ultimi 7 giorni', contextLabel: 'ultimi 7 giorni' },
      { value: '30d', label: 'Ultimi 30 giorni', contextLabel: 'ultimi 30 giorni' },
      { value: '1y', label: 'Ultimi 12 mesi', contextLabel: 'ultimi 12 mesi' }
    ];
  private readonly synchronizedDashboardWidgetIds = [
    'kpi_alerts',
    'alert_types',
    'chart_hierarchy'
  ];
  public weeklyAlertTypesData: IAnalyticsAlertTypes[] = [];
  public weeklyAlertTypesLoading: boolean = true;
  public weeklyEventsOptions: EChartsOption = {};
  public modernAlertTypesOptions: EChartsOption = {};
  public managedEventsGaugeOptions: EChartsOption = {};
  public operationsSummaryLoading = true;
  public operationsSummaryUnavailable = false;
  public operationsSummary?: IAnalyticsOperationsSummary;
  private operationsSummaryRequestId = 0;
  public eventsSummaryLoading = true;
  public eventsSummaryUnavailable = false;
  public eventsSummary?: IAnalyticsEventsSummary;
  private eventsSummaryRequestId = 0;
  public managedEventsDetailsOpen = false;
  public managedEventsDetailsLoading = false;
  public managedEventsDetailsUnavailable = false;
  private managedEventsTrend?: IAnalyticsManagedEventsTrendResponse;
  private managedEventsDetailsRequestId = 0;
  private readonly managedEventsTrendCache = new Map<string, IAnalyticsManagedEventsTrendResponse>();
  public managedEventsDetailsChartOptions: EChartsOption = {};
  public eventTypesDetailsOpen = false;
  public eventTypesDetailsChartOptions: EChartsOption = {};
  public eventTypesComparisonMode: AnalyticsKpiComparisonMode = 'absolute';
  public activeKpiDetail?: AnalyticsKpiDetailType;
  public sensorsDetailsOpen = false;
  private sensorCountRequestId = 0;
  public kpiDetailsLoading = false;
  public kpiDetailsUpdating = false;
  public kpiDetailsUnavailable = false;
  public kpiDetails?: IAnalyticsEventDetailsResponse;
  public kpiDetailsChartOptions: EChartsOption = {};
  public kpiDetailsUsingMock = false;
  public kpiComparisonLevel: AnalyticsKpiComparisonLevel = 'room';
  public kpiComparisonMode: AnalyticsKpiComparisonMode = 'absolute';
  public selectedKpiComparisonIds: string[] = [];
  public kpiComparisonOptions: AnalyticsKpiComparisonOption[] = [];
  public selectedKpiComparisonOptions: AnalyticsKpiComparisonOption[] = [];
  public kpiComparisonOptionsLoading = false;
  public kpiComparisonOptionsUnavailable = false;
  public kpiComparisonSearch = '';
  public kpiComparisonPage = 1;
  public kpiComparisonPageSize = 50;
  public kpiComparisonTotalPages = 1;
  public kpiComparisonTotalItems = 0;
  public readonly maxKpiComparisons = 10;
  public readonly kpiComparisonLevels: ReadonlyArray<{ value: AnalyticsKpiComparisonLevel; label: string }> = [
    { value: 'organization', label: 'Organizzazioni' },
    { value: 'building', label: 'Edifici' },
    { value: 'unit', label: 'Unita' },
    { value: 'room', label: 'Stanze' },
    { value: 'sensor', label: 'Sensori' }
  ];
  private readonly mockKpiComparisonOptions: ReadonlyArray<AnalyticsKpiComparisonOption> = [
    { id: 'organization-serena', label: 'Casa Serena', level: 'organization' },
    { id: 'organization-armonia', label: 'Villa Armonia', level: 'organization' },
    { id: 'organization-aurora', label: 'Residenza Aurora', level: 'organization' },
    { id: 'organization-sole', label: 'Casa del Sole', level: 'organization' },
    { id: 'building-a', label: 'Edificio A', level: 'building' },
    { id: 'building-b', label: 'Edificio B', level: 'building' },
    { id: 'building-c', label: 'Edificio C', level: 'building' },
    { id: 'building-d', label: 'Edificio D', level: 'building' },
    { id: 'unit-alloggio', label: 'Unita Alloggio', level: 'unit' },
    { id: 'unit-protetta', label: 'Unita Protetta', level: 'unit' },
    { id: 'unit-riabilitazione', label: 'Unita Riabilitazione', level: 'unit' },
    { id: 'unit-notturna', label: 'Unita Notturna', level: 'unit' },
    { id: 'room-12', label: 'Camera 12', level: 'room' },
    { id: 'room-08', label: 'Camera 08', level: 'room' },
    { id: 'room-21', label: 'Camera 21', level: 'room' },
    { id: 'room-05', label: 'Camera 05', level: 'room' },
    { id: 'sensor-001', label: 'Sensore camera 12', level: 'sensor' },
    { id: 'sensor-002', label: 'Sensore camera 08', level: 'sensor' },
    { id: 'sensor-003', label: 'Sensore camera 21', level: 'sensor' },
    { id: 'sensor-004', label: 'Sensore camera 05', level: 'sensor' }
  ];
  private kpiDetailsTrendPoints: AnalyticsKpiDetailTrendPoint[] = [];
  private eventsTrend?: IAnalyticsEventsTrendResponse;
  private responseTimeTrend?: IAnalyticsResponseTimeTrendResponse;
  private readonly kpiComparisonColors = [
    '#2774ff', '#14b8a6', '#f59e0b', '#8b5cf6', '#ec4899',
    '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#64748b'
  ];
  private kpiDetailRequestId = 0;
  private kpiComparisonOptionsRequestId = 0;
  private readonly kpiComparisonSearchSubject = new Subject<string>();
  private readonly kpiTrendCache = new Map<string, IAnalyticsEventsTrendResponse | IAnalyticsResponseTimeTrendResponse>();
  private readonly eventComparisonOptionsCache = new Map<string, IAnalyticsEventComparisonOptionsResponse>();
  public readonly dashboardCards: AnalyticsDashboardCardPreference[] = [
    { id: 'kpi_sensors', label: 'Sensori operativi', visible: true },
    { id: 'kpi_events', label: 'Eventi rilevati', visible: true },
    { id: 'kpi_falls', label: 'Cadute rilevate', visible: true },
    { id: 'kpi_response_time', label: 'Tempo mediano di presa in carico', visible: true },
    { id: 'usage_adoption', label: 'Utenti attivi', visible: true, superUserOnly: true },
    { id: 'usage_daily', label: 'Utilizzo giornaliero', visible: true, superUserOnly: true },
    { id: 'event_types', label: 'Tipologie eventi', visible: true },
    { id: 'managed_events_gauge', label: 'Eventi presi in carico', visible: true },
    { id: 'hourly_heatmap', label: 'Fasce orarie piu attive', visible: true },
    { id: 'posture_statistics', label: 'Statistiche posture', visible: true, superUserOnly: true }
  ];
  public heatmapDetailsOpen = false;
  @ViewChild('postureCard') private postureCard?: { detailsOpen: boolean; toggleDetails: () => void };
  public heatmapDetailsLoading = false;
  public heatmapDetailsUnavailable = false;
  private hourlyHeatmapDetails?: IAnalyticsHourlyHeatmapResponse;
  private heatmapDetailsRequestId = 0;
  private readonly hourlyHeatmapCache = new Map<string, IAnalyticsHourlyHeatmapResponse>();
  public readonly heatmapFilterOptions: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'all_alerts', label: 'Tutte le allerte' },
    { value: 'managed_alerts', label: 'Allerte gestite' },
    { value: 'unmanaged_alerts', label: 'Allerte non gestite' }
  ];
  public usageSummaryLoading = true;
  public usageSummaryUnavailable = false;
  public usageSummary?: IUsageAnalyticsSummary;
  public usageAdoptionGaugeOptions: EChartsOption = {};
  private usageSummaryRequestId = 0;
  public usageUsersPreviewLoading = true;
  public usageUsersPreviewUnavailable = false;
  public usageUsersPreview?: IUsageAnalyticsUsersResponse;
  public usageUsersPreviewOptions: EChartsOption = {};
  private usageUsersPreviewRequestId = 0;
  public usageDailyLoading = true;
  public usageDailyUnavailable = false;
  public usageDaily?: IUsageAnalyticsDailyResponse;
  public usageDailyChartOptions: EChartsOption = {};
  private usageDailyRequestId = 0;
  public usageUsersDetailsOpen = false;
  public usageDailyDetailsOpen = false;
  public usageHeatmapLoading = false;
  public usageHeatmapUnavailable = false;
  public usageHeatmapKind: UsageAnalyticsKind = 'application_usage';
  public usageHeatmap?: IUsageAnalyticsHeatmapResponse;
  public usageHeatmapMaxDurationMs = 1;
  private usageHeatmapRequestId = 0;


  private readonly modernAlertPalette: Record<string, string> = {
    bed_out: '#3b82f6',
    bed_in: '#22c6c8',
    room_out: '#76bf43',
    room_in: '#f5a623',
    fall: '#ff4d4f',
    device_offline: '#8b5cf6',
    device_online: '#14b8a6',
    test_notification: '#94a3b8'
  };
  private readonly supportedAlertTypeKeys = new Set([
    'bed_out',
    'bed_in',
    'room_out',
    'room_in',
    'fall'
  ]);
  private readonly modernDashboardWidgetIds = new Set([
    'kpi_sensors',
    'kpi_alerts',
    'chart_weekly',
    'alert_types',
    'chart_hierarchy',
    'heatmap_hourly',
  ]);


  ngOnInit() {
    if (this.analyticsTutorialMode) return;
    this.kpiComparisonSearchSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300)
    ).subscribe(() => {
      if (!this.supportsKpiComparison) return;
      this.kpiComparisonPage = 1;
      this.loadKpiComparisonOptions();
    });
    // 1. Recupero Utente Corrente
    this.currentUser = this.requestsService.getCurrentUser();
    // 2. Logica Differenziata per Ruolo
    if (this.isSuperUser) {
      // Caso SUPERUSER: Carica lista clienti per il filtro
      this.loadCustomers();
    } else {
      // Per gli altri ruoli cliente e organizzazione sono scope fissi, non filtri modificabili.
      if (this.currentUser?.customerId) {
        this.currentContext.customerId = this.currentUser.customerId;
      }

      if (this.currentUser?.organizationId) {
        this.currentContext.organizationId = this.currentUser.organizationId;
        // Carica direttamente gli edifici dell'organizzazione assegnata.
        this.onContextChange('organization');
      }
    }

    // 1. Avvia la logica di caricamento Dashboard
    this.initDashboard();

    // 2. Configura l'Auto-Save 
    this.setupAutoSave();
  }

  // --- LOGICA AUTO-SAVE ---
  private setupAutoSave() {
    this.saveConfigSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(1000),
      switchMap(() => {
        // Prepara il payload leggero (Namespace 'widgets')
        const widgetsState: WidgetState[] = this.widgetRegistry.map(w => ({
          id: w.id,
          visible: w.visible,
          pinned: w.pinned,
          currentFilter: w.currentFilter,
          showLegend: w.showLegend,
          showLabels: w.showLabels
        }));

        const payload = {
          widgets: widgetsState,
          cards: this.dashboardCards
            .filter(card => card.id !== 'posture_statistics' || this.isSuperUser)
            .map(card => ({ id: card.id, visible: card.visible })),
          lastUpdated: new Date()
        };

        console.log('💾 Salvataggio automatico in corso...', payload);
        return this.userSettingsService.saveUserSettings$("analytics", payload);
      })
    ).subscribe({
      next: () => console.log('✅ Preferenze salvate con successo'),
      error: (err) => console.error('❌ Errore salvataggio preferenze', err)
    });
  }

  // --- LOGICA CARICAMENTO DASHBOARD ---
  private initDashboard() {
    this.isDataLoading = true;
    const defaultWidgets = this.getDefaultWidgets();

    this.userSettingsService.loadUserSettings$("analytics").pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        let mergedWidgets = defaultWidgets;

        // Se ci sono impostazioni salvate
        // Nota: Ora controlliamo la chiave 'widgets' nell'oggetto JSONB
        if (res && Array.isArray(res.widgets)) {
          mergedWidgets = defaultWidgets.map(def => {
            const savedState = res.widgets.find((s: WidgetState) => s.id === def.id);
            if (savedState) {
              return {
                ...def,
                visible: savedState.visible ?? def.visible,
                pinned: savedState.pinned ?? def.pinned,
                currentFilter: savedState.currentFilter ?? def.currentFilter,
                showLegend: savedState.showLegend ?? def.showLegend,
                showLabels: savedState.showLabels ?? def.showLabels
              };
            }
            return def;
          });
        }

        this.restoreDashboardPeriod(mergedWidgets);
        this.restoreDashboardCards(res?.cards);
        this.widgetRegistry = mergedWidgets;
        this.loadAllWidgetsData();
        this.isDataLoading = false;
      },
      error: (err) => {
        console.error('Errore caricamento settings, uso default', err);
        this.widgetRegistry = defaultWidgets;
        this.loadAllWidgetsData();
        this.isDataLoading = false;
      }
    });
  }

  private loadAllWidgetsData() {
    this.widgetRegistry.filter(w =>
      w.id !== 'heatmap_hourly'
      && w.id !== 'kpi_response_time'
      && (w.visible || this.modernDashboardWidgetIds.has(w.id))
    ).forEach(widget => {
      this.loadSingleWidgetData(widget);
    });
    this.loadOperationsSummary();
    this.loadEventsSummary();
    this.loadWeeklyAlertTypes();
    this.loadUsageAnalytics();
  }

  private loadEventsSummary(): void {
    const requestId = ++this.eventsSummaryRequestId;
    this.eventsSummaryLoading = true;
    this.eventsSummaryUnavailable = false;

    this.analyticsService.getEventsSummary$(this.newDashboardPeriod, this.currentContext).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        if (requestId !== this.eventsSummaryRequestId) return;
        this.eventsSummary = data;
        this.eventsSummaryLoading = false;
        const levelChanged = this.ensureApplicableKpiComparisonLevel();
        if (this.supportsKpiComparison && (levelChanged || !this.kpiComparisonOptions.length)) {
          this.loadKpiComparisonOptions();
        }
      },
      error: (err) => {
        if (requestId !== this.eventsSummaryRequestId) return;
        console.error('Errore caricamento riepilogo eventi e cadute', err);
        this.eventsSummary = undefined;
        this.eventsSummaryLoading = false;
        this.eventsSummaryUnavailable = true;
      }
    });
  }

  private loadOperationsSummary(): void {
    const requestId = ++this.operationsSummaryRequestId;
    this.operationsSummaryLoading = true;
    this.operationsSummaryUnavailable = false;

    this.analyticsService.getOperationsSummary$(this.newDashboardPeriod, this.currentContext).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        if (requestId !== this.operationsSummaryRequestId) return;
        this.operationsSummary = data;
        this.operationsSummaryLoading = false;
        this.managedEventsGaugeOptions = this.buildManagedEventsGaugeOptions();
      },
      error: (err) => {
        if (requestId !== this.operationsSummaryRequestId) return;
        console.error('Errore caricamento riepilogo operativo', err);
        this.operationsSummary = undefined;
        this.operationsSummaryLoading = false;
        this.operationsSummaryUnavailable = true;
        this.managedEventsGaugeOptions = this.buildManagedEventsGaugeOptions();
      }
    });
  }

  private loadUsageAnalytics(): void {
    if (!this.isSuperUser) {
      this.closeUsageUsersDetails();
      this.closeUsageDailyDetails();
      return;
    }
    this.loadUsageSummary();
    this.loadUsageUsersPreview();
    this.loadUsageDaily();
    if (this.usageDailyDetailsOpen) this.loadUsageHeatmap();
  }

  private loadUsageSummary(): void {
    const requestId = ++this.usageSummaryRequestId;
    this.usageSummaryLoading = true;
    this.usageSummaryUnavailable = false;

    this.analyticsService.getUsageSummary$(this.newDashboardPeriod, this.currentContext).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        if (requestId !== this.usageSummaryRequestId) return;
        this.usageSummary = data;
        this.usageSummaryLoading = false;
        this.usageAdoptionGaugeOptions = this.buildUsageAdoptionGaugeOptions();
      },
      error: (err) => {
        if (requestId !== this.usageSummaryRequestId) return;
        console.error('Errore caricamento riepilogo utilizzo', err);
        this.usageSummary = undefined;
        this.usageSummaryLoading = false;
        this.usageSummaryUnavailable = true;
        this.usageAdoptionGaugeOptions = this.buildUsageAdoptionGaugeOptions();
      }
    });
  }

  private loadUsageUsersPreview(): void {
    const requestId = ++this.usageUsersPreviewRequestId;
    this.usageUsersPreviewLoading = true;
    this.usageUsersPreviewUnavailable = false;

    this.analyticsService.getUsageUsers$(this.newDashboardPeriod, this.currentContext, {
      page: 1,
      pageSize: 5,
      sortBy: 'uniqueDuration',
      sortDirection: 'desc'
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (requestId !== this.usageUsersPreviewRequestId) return;
        this.usageUsersPreview = data;
        this.usageUsersPreviewLoading = false;
        this.usageUsersPreviewOptions = this.buildUsageUsersPreviewOptions();
      },
      error: (err) => {
        if (requestId !== this.usageUsersPreviewRequestId) return;
        console.error('Errore caricamento utilizzo per utente', err);
        this.usageUsersPreview = undefined;
        this.usageUsersPreviewLoading = false;
        this.usageUsersPreviewUnavailable = true;
        this.usageUsersPreviewOptions = {};
      }
    });
  }

  private loadUsageDaily(): void {
    const requestId = ++this.usageDailyRequestId;
    this.usageDailyLoading = true;
    this.usageDailyUnavailable = false;

    this.analyticsService.getUsageDaily$(this.newDashboardPeriod, this.currentContext).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        if (requestId !== this.usageDailyRequestId) return;
        this.usageDaily = data;
        this.usageDailyLoading = false;
        this.usageDailyChartOptions = this.buildUsageDailyChartOptions();
      },
      error: (err) => {
        if (requestId !== this.usageDailyRequestId) return;
        console.error('Errore caricamento utilizzo giornaliero', err);
        this.usageDaily = undefined;
        this.usageDailyLoading = false;
        this.usageDailyUnavailable = true;
        this.usageDailyChartOptions = {};
      }
    });
  }

  public toggleUsageUsersDetails(): void {
    if (!this.isSuperUser) return;
    if (this.usageUsersDetailsOpen) {
      this.closeUsageUsersDetails();
      return;
    }
    this.closeUsageDailyDetails();
    this.usageUsersDetailsOpen = true;
  }

  public closeUsageUsersDetails(): void {
    this.usageUsersDetailsOpen = false;
  }

  public toggleUsageDailyDetails(): void {
    if (!this.isSuperUser) return;
    if (this.usageDailyDetailsOpen) {
      this.closeUsageDailyDetails();
      return;
    }
    this.closeUsageUsersDetails();
    this.usageDailyDetailsOpen = true;
    this.loadUsageHeatmap();
  }

  public closeUsageDailyDetails(): void {
    this.usageHeatmapRequestId++;
    this.usageDailyDetailsOpen = false;
    this.usageHeatmapLoading = false;
    this.usageHeatmapUnavailable = false;
  }

  public onUsageHeatmapKindChange(kind: UsageAnalyticsKind): void {
    if (!this.isSuperUser) return;
    if (kind === this.usageHeatmapKind) return;
    this.usageHeatmapKind = kind;
    this.loadUsageHeatmap();
  }

  private loadUsageHeatmap(): void {
    if (!this.isSuperUser || !this.usageDailyDetailsOpen) return;
    const requestId = ++this.usageHeatmapRequestId;
    this.usageHeatmapLoading = true;
    this.usageHeatmapUnavailable = false;

    this.analyticsService.getUsageHeatmap$(
      this.newDashboardPeriod,
      this.currentContext,
      this.usageHeatmapKind
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (requestId !== this.usageHeatmapRequestId) return;
        this.usageHeatmap = data;
        this.usageHeatmapMaxDurationMs = Math.max(
          1,
          ...data.rows.flatMap(row => row.cells.map(cell => Number(cell.uniqueDurationMs) || 0))
        );
        this.usageHeatmapLoading = false;
      },
      error: (err) => {
        if (requestId !== this.usageHeatmapRequestId) return;
        console.error('Errore caricamento heatmap utilizzo', err);
        this.usageHeatmap = undefined;
        this.usageHeatmapMaxDurationMs = 1;
        this.usageHeatmapLoading = false;
        this.usageHeatmapUnavailable = true;
      }
    });
  }

  private loadWeeklyAlertTypes() {
    this.weeklyAlertTypesLoading = true;
    this.analyticsService.getAlertTypes$('7d', this.currentContext).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.weeklyAlertTypesData = data ?? [];
        this.weeklyAlertTypesLoading = false;
        this.refreshModernChartOptions();
      },
      error: (err) => {
        console.error('Errore caricamento tipologie settimanali', err);
        this.weeklyAlertTypesData = [];
        this.weeklyAlertTypesLoading = false;
        this.refreshModernChartOptions();
      }
    });
  }


  private loadSingleWidgetData(widget: DashboardWidget) {
    const sensorRequestId = widget.id === 'kpi_sensors' ? ++this.sensorCountRequestId : undefined;
    if (sensorRequestId !== undefined) widget.data = undefined;
    widget.loading = true;
    const filterToUse = widget.currentFilter;

    // LOGICA CHIAVE: Passiamo il contesto solo se il widget NON è globale
    const contextToUse = widget.isGlobalOnly ? {} : this.currentContext;

    console.log(`📡 Loading [${widget.id}] | Filter: ${filterToUse} | Global: ${widget.isGlobalOnly}`);

    widget.dataLoader(filterToUse, contextToUse).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (sensorRequestId !== undefined && sensorRequestId !== this.sensorCountRequestId) return;
        widget.data = data;
        widget.loading = false;
        if (widget.id === 'chart_weekly' || widget.id === 'alert_types') {
          this.refreshModernChartOptions();
        }
      },
      error: (err) => {
        if (sensorRequestId !== undefined && sensorRequestId !== this.sensorCountRequestId) return;
        console.error(`Errore widget ${widget.id}`, err);
        this.toastService.showToast('Errore recupero dati', IonicColors.RED);
        widget.loading = false;
        if (widget.id === 'chart_weekly' || widget.id === 'alert_types') {
          this.refreshModernChartOptions();
        }
      }
    });
  }

  private triggerAutoSave() {
    this.saveConfigSubject.next();
  }

  // --- GESTIONE FILTRI GERARCHICI (CASCATA) ---

  // 0. Caricamento iniziale
  loadCustomers() {
    this.customerService.getCustomers$().pipe(
      takeUntil(this.destroy$),
      tap((res) => {
        this.customersList = res;
      })
    ).subscribe()
  }

  /**
   * Gestisce il cambio di valore nelle select a cascata
   * @param level Livello che è cambiato ('customer', 'organization', ecc.)
   */
  onContextChange(level: 'customer' | 'organization' | 'building' | 'unit') {
    this.isContextLoading = true;

    // Logica a cascata: Se cambia il padre, resetto i figli
    switch (level) {
      case 'customer':
        this.currentContext.organizationId = undefined;
        this.currentContext.buildingId = undefined;
        this.currentContext.unitId = undefined;
        this.organizationsList = [];
        this.buildingsList = [];
        this.unitsList = [];

        if (this.currentContext.customerId) {
          // TODO: Carica Organizzazioni del cliente
          this.facilitiesService.getOrganization$(this.currentContext.customerId).pipe(
            takeUntil(this.destroy$),
            tap((res) => {
              this.organizationsList = res;
              this.isContextLoading = false;
            })
          ).subscribe()
        } else {
          this.isContextLoading = false;
        }
        break;

      case 'organization':
        this.currentContext.buildingId = undefined;
        this.currentContext.unitId = undefined;
        this.buildingsList = [];
        this.unitsList = [];

        if (this.currentContext.organizationId) {
          this.facilitiesService.getBuildings$(this.currentContext.organizationId).pipe(
            takeUntil(this.destroy$),
            tap((res) => {
              this.buildingsList = res;
              this.isContextLoading = false;
            })
          ).subscribe()
        } else {
          this.isContextLoading = false;
        }
        break;

      case 'building':
        this.currentContext.unitId = undefined;
        this.unitsList = [];

        if (this.currentContext.buildingId) {
          this.facilitiesService.getUnits$(this.currentContext.buildingId).pipe(
            takeUntil(this.destroy$),
            tap((res) => {
              this.unitsList = res;
              this.isContextLoading = false;
            })
          ).subscribe()
        } else {
          this.isContextLoading = false;
        }
        break;

      case 'unit':
        this.isContextLoading = false;
        break;
    }

    // A prescindere dal livello, ricarichiamo i widget sensibili al contesto
    this.reloadContextAwareWidgets();
  }

  /**
   * Ricarica solo i widget che non sono globali
   */
  private reloadContextAwareWidgets() {
    this.invalidateOperationsDetailCache();
    this.invalidateEventAnalyticsCache();
    this.widgetRegistry.forEach(widget => {
      if (widget.id !== 'heatmap_hourly'
        && widget.id !== 'kpi_response_time'
        && !widget.isGlobalOnly
        && (widget.visible || this.modernDashboardWidgetIds.has(widget.id))) {
        this.loadSingleWidgetData(widget);
      }
    });

    if (this.widgetRegistry.length) {
      this.loadOperationsSummary();
      this.loadEventsSummary();
      this.loadWeeklyAlertTypes();
      this.loadUsageAnalytics();
      if (this.activeKpiDetail) {
        this.openKpiDetails(this.activeKpiDetail);
      }
    }
  }

  /**
   * Resetta completamente i filtri
   */
  resetContext() {
    if (this.isSuperUser) {
      // SuperUser: Resetta tutto a zero
      this.currentContext = {
        customerId: undefined,
        organizationId: undefined,
        buildingId: undefined,
        unitId: undefined
      };
      this.organizationsList = [];
    } else {
      // Gli scope utente restano fissi; vengono azzerati solo i filtri modificabili.
      this.currentContext.customerId = this.currentUser?.customerId;
      this.currentContext.organizationId = this.currentUser?.organizationId;
      this.currentContext.buildingId = undefined;
      this.currentContext.unitId = undefined;
    }
    if (this.isSuperUser) this.buildingsList = [];
    this.unitsList = [];

    // Ricarica i dati globali
    this.reloadContextAwareWidgets();
  }

  get hasActiveFilters(): boolean {
    // Cliente/organizzazione sono filtri per il superuser e scope fissi per gli altri ruoli.
    if (this.isSuperUser) return !!this.currentContext.customerId;
    return !!this.currentContext.buildingId || !!this.currentContext.unitId;
  }

  // --- INTERAZIONI WIDGET ---

  togglePin(widgetId: string) {
    const widget = this.widgetRegistry.find(w => w.id === widgetId);
    if (widget) {
      widget.pinned = !widget.pinned;
      this.triggerAutoSave();
    }
  }



  onNewDashboardPeriodChange(filter: AnalyticsPeriod) {
    if (filter === this.newDashboardPeriod) return;

    this.newDashboardPeriod = filter;
    this.invalidateOperationsDetailCache();
    this.kpiTrendCache.clear();
    this.weeklyEventsOptions = this.buildWeeklyEventsOptions();
    this.loadOperationsSummary();
    this.loadEventsSummary();
    this.loadUsageAnalytics();
    if (this.managedEventsDetailsOpen) {
      this.loadManagedEventsDetails();
    }
    if (this.heatmapDetailsOpen) {
      this.loadHeatmapDetails();
    }
    if (this.eventTypesDetailsOpen) {
      this.rebuildEventTypesDetailsChart();
    }

    this.synchronizedDashboardWidgetIds.forEach(widgetId => {
      const widget = this.getWidget(widgetId);
      if (!widget?.availableFilters?.includes(filter)) return;

      widget.currentFilter = filter;
      widget.data = undefined;
      this.loadSingleWidgetData(widget);
    });

    if (this.activeKpiDetail) {
      if (this.isEventKpiDetail) {
        this.loadKpiTrend();
      } else {
        this.openKpiDetails(this.activeKpiDetail);
      }
    }
    this.triggerAutoSave();
  }

  public toggleKpiDetails(type: AnalyticsKpiDetailType): void {
    this.sensorsDetailsOpen = false;
    if (this.activeKpiDetail === type) {
      this.closeKpiDetails();
      return;
    }
    this.openKpiDetails(type);
  }

  public openKpiDetails(type: AnalyticsKpiDetailType): void {
    ++this.kpiDetailRequestId;
    this.activeKpiDetail = type;
    this.kpiDetailsLoading = true;
    this.kpiDetailsUpdating = false;
    this.kpiDetailsUnavailable = false;
    this.kpiDetailsUsingMock = false;
    this.kpiDetails = undefined;
    this.kpiDetailsChartOptions = {};
    this.eventsTrend = undefined;
    this.responseTimeTrend = undefined;

    this.resetKpiComparisonState(false);
    this.ensureApplicableKpiComparisonLevel();
    if (this.supportsKpiComparison) {
      this.loadKpiComparisonOptions();
    }
    this.loadKpiTrend();
  }

  public closeKpiDetails(): void {
    this.kpiDetailRequestId++;
    this.activeKpiDetail = undefined;
    this.kpiDetails = undefined;
    this.kpiDetailsChartOptions = {};
    this.kpiDetailsTrendPoints = [];
    this.eventsTrend = undefined;
    this.responseTimeTrend = undefined;
    this.kpiDetailsUsingMock = false;
    this.kpiDetailsLoading = false;
    this.kpiDetailsUpdating = false;
    this.kpiDetailsUnavailable = false;
  }

  @HostListener('document:keydown.escape')
  public closeKpiDetailsOnEscape(): void {
    if (this.activeKpiDetail) {
      this.closeKpiDetails();
    }
    if (this.heatmapDetailsOpen) {
      this.closeHeatmapDetails();
    }
    if (this.eventTypesDetailsOpen) {
      this.closeEventTypesDetails();
    }
    if (this.managedEventsDetailsOpen) {
      this.closeManagedEventsDetails();
    }
    if (this.usageUsersDetailsOpen) {
      this.closeUsageUsersDetails();
    }
    if (this.usageDailyDetailsOpen) {
      this.closeUsageDailyDetails();
    }
  }

  public toggleHeatmapDetails(): void {
    if (this.heatmapDetailsOpen) {
      this.closeHeatmapDetails();
      return;
    }

    this.closeEventTypesDetails();
    this.heatmapDetailsOpen = true;
    this.loadHeatmapDetails();
  }

  public closeHeatmapDetails(): void {
    this.heatmapDetailsRequestId++;
    this.heatmapDetailsOpen = false;
    this.heatmapDetailsLoading = false;
    this.heatmapDetailsUnavailable = false;
    this.hourlyHeatmapDetails = undefined;
  }

  public toggleEventTypesDetails(): void {
    if (this.eventTypesDetailsOpen) {
      this.closeEventTypesDetails();
      return;
    }

    this.closeHeatmapDetails();
    this.eventTypesDetailsOpen = true;
    this.rebuildEventTypesDetailsChart();
  }

  public closeEventTypesDetails(): void {
    this.eventTypesDetailsOpen = false;
    this.eventTypesDetailsChartOptions = {};
  }

  public toggleManagedEventsDetails(): void {
    if (this.managedEventsDetailsOpen) {
      this.closeManagedEventsDetails();
      return;
    }

    if (this.postureCard?.detailsOpen) this.postureCard.toggleDetails();
    this.managedEventsDetailsOpen = true;
    this.loadManagedEventsDetails();
  }

  public closeManagedEventsDetails(): void {
    this.managedEventsDetailsRequestId++;
    this.managedEventsDetailsOpen = false;
    this.managedEventsDetailsLoading = false;
    this.managedEventsDetailsUnavailable = false;
    this.managedEventsTrend = undefined;
    this.managedEventsDetailsChartOptions = {};
  }

  public onPostureDetailsChange(open: boolean): void {
    if (open) this.closeManagedEventsDetails();
  }

  private loadManagedEventsDetails(): void {
    if (!this.managedEventsDetailsOpen) return;

    const requestId = ++this.managedEventsDetailsRequestId;
    const cacheKey = this.operationsDetailCacheKey();
    const cached = this.managedEventsTrendCache.get(cacheKey);
    if (cached) {
      this.managedEventsTrend = cached;
      this.managedEventsDetailsLoading = false;
      this.managedEventsDetailsUnavailable = false;
      this.rebuildManagedEventsDetailsChart();
      return;
    }

    this.managedEventsDetailsLoading = true;
    this.managedEventsDetailsUnavailable = false;
    this.managedEventsDetailsChartOptions = {};

    this.analyticsService.getManagedEventsTrend$(this.newDashboardPeriod, this.currentContext).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        if (requestId !== this.managedEventsDetailsRequestId) return;
        this.managedEventsTrend = data;
        this.managedEventsTrendCache.set(cacheKey, data);
        this.managedEventsDetailsLoading = false;
        this.rebuildManagedEventsDetailsChart();
      },
      error: (err) => {
        if (requestId !== this.managedEventsDetailsRequestId) return;
        console.error('Errore caricamento dettaglio eventi presi in carico', err);
        this.managedEventsTrend = undefined;
        this.managedEventsDetailsLoading = false;
        this.managedEventsDetailsUnavailable = true;
      }
    });
  }

  private loadHeatmapDetails(): void {
    if (!this.heatmapDetailsOpen) return;

    const requestId = ++this.heatmapDetailsRequestId;
    const cacheKey = this.operationsDetailCacheKey(this.heatmapStatus);
    const cached = this.hourlyHeatmapCache.get(cacheKey);
    if (cached) {
      this.hourlyHeatmapDetails = cached;
      this.heatmapDetailsLoading = false;
      this.heatmapDetailsUnavailable = false;
      return;
    }

    this.heatmapDetailsLoading = true;
    this.heatmapDetailsUnavailable = false;

    this.analyticsService.getHourlyHeatmapDetails$(
      this.newDashboardPeriod,
      this.heatmapStatus,
      this.currentContext
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (requestId !== this.heatmapDetailsRequestId) return;
        this.hourlyHeatmapDetails = data;
        this.hourlyHeatmapCache.set(cacheKey, data);
        this.heatmapDetailsLoading = false;
      },
      error: (err) => {
        if (requestId !== this.heatmapDetailsRequestId) return;
        console.error('Errore caricamento dettaglio fasce orarie', err);
        this.hourlyHeatmapDetails = undefined;
        this.heatmapDetailsLoading = false;
        this.heatmapDetailsUnavailable = true;
      }
    });
  }

  private operationsDetailCacheKey(status?: AnalyticsHourlyHeatmapStatus): string {
    return JSON.stringify({
      period: this.newDashboardPeriod,
      customerId: this.currentContext.customerId ?? null,
      organizationId: this.currentContext.organizationId ?? null,
      buildingId: this.currentContext.buildingId ?? null,
      unitId: this.currentContext.unitId ?? null,
      status: status ?? null
    });
  }

  private invalidateOperationsDetailCache(): void {
    this.managedEventsTrendCache.clear();
    this.hourlyHeatmapCache.clear();
  }

  public setEventTypesComparisonMode(mode: AnalyticsKpiComparisonMode): void {
    if (mode === this.eventTypesComparisonMode) return;
    this.eventTypesComparisonMode = mode;
    this.rebuildEventTypesDetailsChart();
  }
  public isKpiDetailOpen(type: AnalyticsKpiDetailType): boolean {
    return this.activeKpiDetail === type;
  }

  public get activeKpiDetailTitle(): string {
    const titles: Record<AnalyticsKpiDetailType, string> = {
      events: 'Dettaglio eventi rilevati',
      falls: 'Dettaglio cadute rilevate',
      responseTime: 'Dettaglio tempo mediano di presa in carico'
    };
    return this.activeKpiDetail ? titles[this.activeKpiDetail] : '';
  }

  public get activeKpiDetailSummaryLabel(): string {
    const labels: Record<AnalyticsKpiDetailType, string> = {
      events: 'eventi nel periodo',
      falls: 'cadute nel periodo',
      responseTime: 'eventi presi in carico'
    };
    return this.activeKpiDetail ? labels[this.activeKpiDetail] : '';
  }

  public get activeKpiDetailSensorsTitle(): string {
    return this.activeKpiDetail === 'responseTime'
      ? 'Sensori con piu prese in carico'
      : this.activeKpiDetail === 'falls'
        ? 'Sensori con piu cadute'
        : 'Sensori con piu eventi';
  }

  public get activeKpiDetailEventsTitle(): string {
    return this.activeKpiDetail === 'responseTime'
      ? 'Tempi recenti'
      : 'Eventi recenti';
  }

  public get activeKpiDetailChartTitle(): string {
    const titles: Record<AnalyticsKpiDetailType, string> = {
      events: 'Andamento degli eventi',
      falls: 'Andamento delle cadute',
      responseTime: 'Andamento del tempo mediano'
    };
    return this.activeKpiDetail ? titles[this.activeKpiDetail] : '';
  }

  public get activeKpiDetailGranularityLabel(): string {
    return this.newDashboardPeriod === '24h'
      ? 'Per ora'
      : this.newDashboardPeriod === '1y'
        ? 'Per mese'
        : 'Per giorno';
  }

  public get hasKpiDetailsChart(): boolean {
    return Boolean((this.kpiDetailsChartOptions.series as unknown[])?.length);
  }

  public get isEventKpiDetail(): boolean {
    return Boolean(this.activeKpiDetail);
  }

  public get supportsKpiComparison(): boolean {
    return this.activeKpiDetail === 'events' || this.activeKpiDetail === 'falls';
  }

  public get availableKpiComparisonLevels(): ReadonlyArray<{ value: AnalyticsKpiComparisonLevel; label: string }> {
    if (!this.supportsKpiComparison) return [];
    const available = new Set(this.eventsSummary?.availableComparisonLevels ?? []);
    return this.kpiComparisonLevels.filter(level => available.has(level.value));
  }

  public get activeKpiComparisonLevelLabel(): string {
    return this.kpiComparisonLevels.find(level => level.value === this.kpiComparisonLevel)?.label ?? 'Stanze';
  }

  public isKpiComparisonSelected(id: string): boolean {
    return this.selectedKpiComparisonIds.includes(id);
  }

  public canSelectKpiComparison(id: string): boolean {
    return this.isKpiComparisonSelected(id)
      || this.selectedKpiComparisonIds.length < this.maxKpiComparisons;
  }

  public onKpiComparisonLevelChange(level: AnalyticsKpiComparisonLevel): void {
    if (level === this.kpiComparisonLevel) return;
    this.kpiComparisonLevel = level;
    this.selectedKpiComparisonIds = [];
    this.selectedKpiComparisonOptions = [];
    this.kpiComparisonSearch = '';
    this.kpiComparisonPage = 1;

    if (this.isEventKpiDetail) {
      this.loadKpiComparisonOptions();
      this.loadKpiTrend();
      return;
    }

    this.kpiComparisonOptions = this.mockKpiComparisonOptions.filter(option => option.level === level);
    this.selectedKpiComparisonOptions = this.kpiComparisonOptions.slice(0, 2);
    this.selectedKpiComparisonIds = this.selectedKpiComparisonOptions.map(option => option.id);
    this.rebuildKpiDetailsChart();
  }

  public toggleKpiComparison(id: string): void {
    const previousIds = [...this.selectedKpiComparisonIds];
    const previousOptions = [...this.selectedKpiComparisonOptions];
    if (this.isKpiComparisonSelected(id)) {
      this.selectedKpiComparisonIds = this.selectedKpiComparisonIds.filter(selectedId => selectedId !== id);
      this.selectedKpiComparisonOptions = this.selectedKpiComparisonOptions.filter(option => option.id !== id);
    } else if (this.selectedKpiComparisonIds.length < this.maxKpiComparisons) {
      const option = this.kpiComparisonOptions.find(item => item.id === id);
      if (!option) return;
      this.selectedKpiComparisonIds = [...this.selectedKpiComparisonIds, id];
      this.selectedKpiComparisonOptions = [...this.selectedKpiComparisonOptions, option];
    }
    this.isEventKpiDetail
      ? this.loadKpiTrend(true, () => {
          this.selectedKpiComparisonIds = previousIds;
          this.selectedKpiComparisonOptions = previousOptions;
        })
      : this.rebuildKpiDetailsChart();
  }

  public removeKpiComparison(id: string): void {
    const previousIds = [...this.selectedKpiComparisonIds];
    const previousOptions = [...this.selectedKpiComparisonOptions];
    this.selectedKpiComparisonIds = this.selectedKpiComparisonIds.filter(selectedId => selectedId !== id);
    this.selectedKpiComparisonOptions = this.selectedKpiComparisonOptions.filter(option => option.id !== id);
    this.isEventKpiDetail
      ? this.loadKpiTrend(true, () => {
          this.selectedKpiComparisonIds = previousIds;
          this.selectedKpiComparisonOptions = previousOptions;
        })
      : this.rebuildKpiDetailsChart();
  }

  public setKpiComparisonMode(mode: AnalyticsKpiComparisonMode): void {
    if (mode === this.kpiComparisonMode) return;
    this.kpiComparisonMode = mode;
    this.rebuildKpiDetailsChart();
  }

  public kpiComparisonColor(id: string): string {
    const index = this.selectedKpiComparisonIds.indexOf(id);
    return this.kpiComparisonColors[Math.max(0, index) % this.kpiComparisonColors.length];
  }

  private rebuildKpiDetailsChart(): void {
    if (this.isEventKpiDetail) {
      this.kpiDetailsChartOptions = this.activeKpiDetail === 'responseTime'
        ? (this.responseTimeTrend ? this.buildResponseTimeTrendChartOptions(this.responseTimeTrend) : {})
        : (this.eventsTrend ? this.buildEventKpiTrendChartOptions(this.eventsTrend) : {});
      return;
    }

    if (!this.activeKpiDetail || !this.kpiDetailsTrendPoints.length) {
      this.kpiDetailsChartOptions = {};
      return;
    }
    this.kpiDetailsChartOptions = this.buildKpiDetailsChartOptions(
      this.activeKpiDetail,
      this.kpiDetailsTrendPoints
    );
  }

  public onKpiComparisonSearchChange(value: string): void {
    this.kpiComparisonSearch = value;
    this.kpiComparisonSearchSubject.next(value);
  }

  public goToKpiComparisonPage(page: number): void {
    if (page < 1 || page > this.kpiComparisonTotalPages || page === this.kpiComparisonPage) return;
    this.kpiComparisonPage = page;
    this.loadKpiComparisonOptions();
  }

  private resetKpiComparisonState(useMockOptions: boolean): void {
    this.selectedKpiComparisonIds = [];
    this.selectedKpiComparisonOptions = [];
    this.kpiComparisonSearch = '';
    this.kpiComparisonPage = 1;
    this.kpiComparisonTotalPages = 1;
    this.kpiComparisonTotalItems = 0;
    this.kpiComparisonOptionsUnavailable = false;
    this.kpiComparisonOptions = useMockOptions
      ? this.mockKpiComparisonOptions.filter(option => option.level === this.kpiComparisonLevel)
      : [];

    if (useMockOptions) {
      this.selectedKpiComparisonOptions = this.kpiComparisonOptions.slice(0, 2);
      this.selectedKpiComparisonIds = this.selectedKpiComparisonOptions.map(option => option.id);
    }
  }

  private ensureApplicableKpiComparisonLevel(): boolean {
    const levels = this.eventsSummary?.availableComparisonLevels ?? [];
    if (!levels.length || levels.includes(this.kpiComparisonLevel)) return false;

    const preferredOrder: AnalyticsKpiComparisonLevel[] = ['room', 'sensor', 'unit', 'building', 'organization'];
    const nextLevel = preferredOrder.find(level => levels.includes(level));
    if (!nextLevel) return false;
    this.kpiComparisonLevel = nextLevel;
    this.selectedKpiComparisonIds = [];
    this.selectedKpiComparisonOptions = [];
    return true;
  }

  private loadKpiComparisonOptions(): void {
    if (!this.supportsKpiComparison || !this.availableKpiComparisonLevels.some(level => level.value === this.kpiComparisonLevel)) {
      this.kpiComparisonOptions = [];
      return;
    }

    const requestId = ++this.kpiComparisonOptionsRequestId;
    const cacheKey = this.eventComparisonOptionsCacheKey();
    const cached = this.eventComparisonOptionsCache.get(cacheKey);
    if (cached) {
      this.applyKpiComparisonOptions(cached);
      return;
    }

    this.kpiComparisonOptionsLoading = true;
    this.kpiComparisonOptionsUnavailable = false;
    this.analyticsService.getEventComparisonOptions$(
      this.kpiComparisonLevel,
      this.currentContext,
      this.kpiComparisonSearch,
      this.kpiComparisonPage,
      this.kpiComparisonPageSize
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (requestId !== this.kpiComparisonOptionsRequestId) return;
        this.eventComparisonOptionsCache.set(cacheKey, data);
        this.applyKpiComparisonOptions(data);
      },
      error: (err) => {
        if (requestId !== this.kpiComparisonOptionsRequestId) return;
        console.error('Errore caricamento opzioni di confronto', err);
        this.kpiComparisonOptions = [];
        this.kpiComparisonOptionsLoading = false;
        this.kpiComparisonOptionsUnavailable = true;
      }
    });
  }

  private applyKpiComparisonOptions(data: IAnalyticsEventComparisonOptionsResponse): void {
    this.kpiComparisonOptions = (data.items ?? []).map(item => ({
      ...item,
      level: data.level
    }));
    this.kpiComparisonPage = Number(data.pagination?.page ?? 1);
    this.kpiComparisonTotalPages = Math.max(1, Number(data.pagination?.totalPages ?? 1));
    this.kpiComparisonTotalItems = Number(data.pagination?.totalItems ?? 0);
    this.kpiComparisonOptionsLoading = false;
    this.kpiComparisonOptionsUnavailable = false;
  }

  private loadKpiTrend(preserveChart = false, rollbackSelection?: () => void): void {
    const type = this.activeKpiDetail;
    if (!type) return;
    const requestId = ++this.kpiDetailRequestId;
    const cacheKey = this.kpiTrendCacheKey(type);
    const cached = this.kpiTrendCache.get(cacheKey);
    if (cached) {
      this.applyKpiTrend(cached);
      return;
    }

    const hasCurrentTrend = type === 'responseTime' ? Boolean(this.responseTimeTrend) : Boolean(this.eventsTrend);
    this.kpiDetailsLoading = !preserveChart || !hasCurrentTrend;
    this.kpiDetailsUpdating = preserveChart && hasCurrentTrend;
    this.kpiDetailsUnavailable = false;
    const seriesGroups = this.chunkKpiComparisonIds(this.selectedKpiComparisonIds);
    const buildRequest = (seriesIds: string[]): Observable<IAnalyticsEventsTrendResponse | IAnalyticsResponseTimeTrendResponse> => {
      const comparisonLevel = seriesIds.length ? this.kpiComparisonLevel : undefined;
      return type === 'responseTime'
        ? this.analyticsService.getResponseTimeTrend$(
            this.newDashboardPeriod,
            this.currentContext,
            comparisonLevel,
            seriesIds
          )
        : this.analyticsService.getEventsTrend$(
            type === 'falls' ? 'fall' : 'all',
            this.newDashboardPeriod,
            this.currentContext,
            comparisonLevel,
            seriesIds
          );
    };
    const requests = (seriesGroups.length ? seriesGroups : [[]]).map(buildRequest);
    const request$ = requests.length === 1
      ? requests[0]
      : forkJoin(requests).pipe(map(responses => type === 'responseTime'
          ? this.mergeResponseTimeTrendResponses(responses as IAnalyticsResponseTimeTrendResponse[])
          : this.mergeEventTrendResponses(responses as IAnalyticsEventsTrendResponse[])));

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (requestId !== this.kpiDetailRequestId) return;
        this.kpiTrendCache.set(cacheKey, data);
        this.applyKpiTrend(data);
      },
      error: (err) => {
        if (requestId !== this.kpiDetailRequestId) return;
        console.error('Errore caricamento trend ' + type, err);
        this.kpiDetailsUpdating = false;
        if (preserveChart && hasCurrentTrend) {
          rollbackSelection?.();
          this.toastService.showToast('Impossibile aggiornare le serie del grafico', IonicColors.RED);
          return;
        }
        this.eventsTrend = undefined;
        this.responseTimeTrend = undefined;
        this.kpiDetails = undefined;
        this.kpiDetailsChartOptions = {};
        this.kpiDetailsLoading = false;
        this.kpiDetailsUnavailable = true;
      }
    });
  }

  private applyKpiTrend(data: IAnalyticsEventsTrendResponse | IAnalyticsResponseTimeTrendResponse): void {
    const isResponseTime = !('eventType' in data);
    this.eventsTrend = isResponseTime ? undefined : data;
    this.responseTimeTrend = isResponseTime ? data : undefined;
    const totalSeries = data.series?.find(series => series.isTotal);
    const total = isResponseTime
      ? (totalSeries as IAnalyticsResponseTimeTrendResponse['series'][number] | undefined)?.sampleSize ?? 0
      : (totalSeries as IAnalyticsEventsTrendResponse['series'][number] | undefined)?.total ?? 0;
    this.kpiDetails = { total: Number(total), topSensors: [], events: [] };
    this.kpiDetailsLoading = false;
    this.kpiDetailsUpdating = false;
    this.kpiDetailsUnavailable = false;
    this.rebuildKpiDetailsChart();
  }

  private chunkKpiComparisonIds(ids: string[]): string[][] {
    const backendSeriesLimit = 4;
    const groups: string[][] = [];
    for (let index = 0; index < ids.length; index += backendSeriesLimit) {
      groups.push(ids.slice(index, index + backendSeriesLimit));
    }
    return groups;
  }

  private mergeEventTrendResponses(responses: IAnalyticsEventsTrendResponse[]): IAnalyticsEventsTrendResponse {
    const first = responses[0];
    const total = first.series.find(series => series.isTotal);
    const comparedSeries = responses.flatMap(response => response.series.filter(series => !series.isTotal));
    return {
      ...first,
      comparisonLevel: this.kpiComparisonLevel,
      series: [...(total ? [total] : []), ...comparedSeries]
    };
  }

  private mergeResponseTimeTrendResponses(
    responses: IAnalyticsResponseTimeTrendResponse[]
  ): IAnalyticsResponseTimeTrendResponse {
    const first = responses[0];
    const total = first.series.find(series => series.isTotal);
    const comparedSeries = responses.flatMap(response => response.series.filter(series => !series.isTotal));
    return {
      ...first,
      comparisonLevel: this.kpiComparisonLevel,
      series: [...(total ? [total] : []), ...comparedSeries]
    };
  }

  private kpiTrendCacheKey(type: AnalyticsKpiDetailType): string {
    return JSON.stringify({
      type,
      period: this.newDashboardPeriod,
      context: this.currentContext,
      level: this.selectedKpiComparisonIds.length ? this.kpiComparisonLevel : null,
      seriesIds: this.selectedKpiComparisonIds
    });
  }

  private eventComparisonOptionsCacheKey(): string {
    return JSON.stringify({
      context: this.currentContext,
      level: this.kpiComparisonLevel,
      search: this.kpiComparisonSearch.trim().toLocaleLowerCase('it-IT'),
      page: this.kpiComparisonPage,
      pageSize: this.kpiComparisonPageSize
    });
  }

  private invalidateEventAnalyticsCache(): void {
    this.kpiTrendCache.clear();
    this.eventComparisonOptionsCache.clear();
    this.kpiComparisonOptionsRequestId++;
  }

  public get kpiDetailsEvents(): IAnalyticsEventDetail[] {
    return this.kpiDetails?.events ?? [];
  }

  public sensorBarWidth(count: number): number {
    const max = Math.max(0, ...(this.kpiDetails?.topSensors ?? []).map(sensor => Number(sensor.count)));
    return max ? Math.max(6, Math.round((Number(count) / max) * 100)) : 0;
  }

  public formatEventTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp || '-';
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  public eventLocation(event: IAnalyticsEventDetail): string {
    return [event.building, event.unit, event.room, event.bed]
      .filter(Boolean)
      .join(' / ') || 'Posizione non disponibile';
  }

  public formatDuration(seconds: number | undefined): string {
    if (seconds === undefined || seconds === null || !Number.isFinite(Number(seconds))) return '-';
    const total = Math.max(0, Math.round(Number(seconds)));
    if (total < 60) return `${total} sec`;
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const remainingSeconds = total % 60;
    if (hours) return `${hours} h ${minutes} min`;
    return remainingSeconds ? `${minutes} min ${remainingSeconds} sec` : `${minutes} min`;
  }


  private applyMockKpiDetails(type: AnalyticsKpiDetailType): void {
    const mock = this.buildMockKpiDetails(type, this.newDashboardPeriod);
    this.kpiDetails = mock.details;
    this.kpiDetailsTrendPoints = mock.points;
    this.rebuildKpiDetailsChart();
    this.kpiDetailsUsingMock = true;
    this.kpiDetailsUnavailable = false;
    this.kpiDetailsLoading = false;
  }

  private buildMockKpiDetails(
    type: AnalyticsKpiDetailType,
    period: AnalyticsPeriod
  ): AnalyticsKpiDetailMock {
    const points = this.buildMockTrendPoints(type, period);
    const totalsByPeriod: Record<AnalyticsPeriod, number> = {
      '24h': 18,
      '7d': 126,
      '30d': 548,
      '1y': 6840
    };
    const total = type === 'responseTime'
      ? totalsByPeriod[period]
      : points.reduce((sum, point) => sum + point.value, 0);
    const sensorNames = [
      'Sensore camera 12',
      'Sensore camera 08',
      'Sensore camera 21',
      'Sensore camera 05'
    ];
    const sensorShares = [0.38, 0.27, 0.2, 0.1];
    const topSensors = sensorNames.map((sensorName, index) => ({
      sensorId: 'S-' + String(index + 1).padStart(3, '0'),
      sensorName,
      count: Math.max(1, Math.round(total * sensorShares[index]))
    }));

    const eventCount = 6;
    const now = Date.now();
    const events: IAnalyticsEventDetail[] = Array.from({ length: eventCount }, (_, index) => {
      const managed = type === 'responseTime' || index % 4 !== 2;
      const responseTimeSeconds = managed
        ? 68 + ((index * 29 + (type === 'falls' ? 17 : 0)) % 145)
        : undefined;
      const timestamp = new Date(now - (index + 1) * 47 * 60 * 1000);

      return {
        id: 'demo-' + type + '-' + (index + 1),
        timestamp: timestamp.toISOString(),
        sensorId: 'S-' + String((index % sensorNames.length) + 1).padStart(3, '0'),
        sensorName: sensorNames[index % sensorNames.length],
        building: index % 2 ? 'Edificio B' : 'Edificio A',
        unit: 'Unita Alloggio',
        room: 'Camera ' + String(12 - index).padStart(2, '0'),
        bed: 'Letto ' + ((index % 2) + 1),
        managed,
        managedAt: managed && responseTimeSeconds
          ? new Date(timestamp.getTime() + responseTimeSeconds * 1000).toISOString()
          : undefined,
        managedBy: managed ? (index % 2 ? 'Operatore turno B' : 'Operatore turno A') : undefined,
        responseTimeSeconds
      };
    });

    return {
      details: { total, topSensors, events },
      points
    };
  }

  private buildMockTrendPoints(
    type: AnalyticsKpiDetailType,
    period: AnalyticsPeriod
  ): AnalyticsKpiDetailTrendPoint[] {
    const bucketCount: Record<AnalyticsPeriod, number> = {
      '24h': 24,
      '7d': 7,
      '30d': 30,
      '1y': 12
    };
    const count = bucketCount[period];
    const now = new Date();

    return Array.from({ length: count }, (_, index) => {
      const date = new Date(now);
      if (period === '24h') {
        date.setMinutes(0, 0, 0);
        date.setHours(date.getHours() - (count - 1 - index));
      } else if (period === '1y') {
        date.setHours(12, 0, 0, 0);
        date.setDate(1);
        date.setMonth(date.getMonth() - (count - 1 - index));
      } else {
        date.setHours(12, 0, 0, 0);
        date.setDate(date.getDate() - (count - 1 - index));
      }

      let value: number;
      if (type === 'responseTime') {
        value = Math.round(92 + ((index * 19 + count) % 72) + Math.sin(index * 1.25) * 15);
      } else if (type === 'falls') {
        const scale: Record<AnalyticsPeriod, number> = { '24h': 1, '7d': 3, '30d': 4, '1y': 24 };
        value = Math.max(0, Math.round(scale[period] * (0.45 + ((index * 5) % 8) / 8)));
      } else {
        const scale: Record<AnalyticsPeriod, number> = { '24h': 4, '7d': 32, '30d': 35, '1y': 760 };
        value = Math.max(0, Math.round(scale[period] * (0.72 + ((index * 7) % 9) / 18 + Math.sin(index) * 0.1)));
      }

      const label = period === '24h'
        ? new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' }).format(date)
        : period === '1y'
          ? new Intl.DateTimeFormat('it-IT', { month: 'short', year: '2-digit' }).format(date)
          : new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short' }).format(date);

      return { label, value };
    });
  }

  private buildKpiDetailsChartOptions(
    type: AnalyticsKpiDetailType,
    points: AnalyticsKpiDetailTrendPoint[]
  ): EChartsOption {
    const isResponseTime = type === 'responseTime';
    const isVariation = this.kpiComparisonMode === 'variation';
    const metricColor = type === 'falls' ? '#ff5b5d' : '#2774ff';
    const selectedOptions = this.selectedKpiComparisonOptions;
    const hasComparisons = selectedOptions.length > 0;
    const xAxisLabelStep = this.newDashboardPeriod === '24h'
      ? 4
      : this.newDashboardPeriod === '30d'
        ? 5
        : 1;
    const lastPointIndex = Math.max(0, points.length - 1);
    const totalValues = points.map(point => point.value);
    const seriesDefinitions = [
      {
        name: 'Totale struttura',
        color: hasComparisons ? '#7b8798' : metricColor,
        values: totalValues,
        dashed: hasComparisons,
        total: true
      },
      ...selectedOptions.map((option, index) => ({
        name: option.label,
        color: this.kpiComparisonColors[index % this.kpiComparisonColors.length],
        values: this.buildMockComparisonValues(type, totalValues, option, index),
        dashed: false,
        total: false
      }))
    ].map(definition => ({
      ...definition,
      values: isVariation
        ? this.toPercentageVariation(definition.values)
        : definition.values
    }));

    return {
      animationDuration: 350,
      color: seriesDefinitions.map(series => series.color),
      grid: { top: 18, right: 22, bottom: 36, left: 58, containLabel: false },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e2e7ef',
        borderWidth: 1,
        textStyle: { color: '#26334d', fontSize: 11 },
        valueFormatter: (value: any) => {
          const numericValue = Number(value);
          if (isVariation) {
            const sign = numericValue > 0 ? '+' : '';
            return sign + numericValue.toLocaleString('it-IT', { maximumFractionDigits: 1 }) + '%';
          }
          return isResponseTime
            ? this.formatDuration(numericValue)
            : numericValue.toLocaleString('it-IT');
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: points.map(point => point.label),
        axisTick: {
          show: true,
          alignWithLabel: true,
          interval: 0,
          lineStyle: { color: '#dfe5ee' }
        },
        axisLine: { lineStyle: { color: '#dfe5ee' } },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          showMinLabel: true,
          showMaxLabel: true,
          interval: (index: number) =>
            index === 0 || index === lastPointIndex || index % xAxisLabelStep === 0
        }
      },
      yAxis: {
        type: 'value',
        min: isVariation ? undefined : 0,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          formatter: (value: number) => {
            if (isVariation) {
              const sign = value > 0 ? '+' : '';
              return sign + value.toLocaleString('it-IT', { maximumFractionDigits: 0 }) + '%';
            }
            return isResponseTime
              ? this.formatDuration(value)
              : value.toLocaleString('it-IT');
          }
        },
        splitLine: {
          lineStyle: {
            color: '#edf0f5',
            type: 'dashed'
          }
        }
      },
      series: seriesDefinitions.map(definition => ({
        name: definition.name,
        type: 'line' as const,
        smooth: 0.2,
        connectNulls: true,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: points.length > 12 ? 4 : 7,
        data: definition.values,
        lineStyle: {
          width: definition.total && hasComparisons ? 2 : 2.5,
          color: definition.color,
          type: definition.dashed ? 'dashed' : 'solid'
        },
        itemStyle: {
          color: definition.color,
          borderColor: '#fff',
          borderWidth: 1.5
        },
        areaStyle: !hasComparisons && definition.total && !isVariation
          ? { color: type === 'falls' ? 'rgba(255, 91, 93, .10)' : 'rgba(39, 116, 255, .10)' }
          : undefined,
        emphasis: { focus: 'series' as const }
      }))
    };
  }

  private buildEventKpiTrendChartOptions(trend: IAnalyticsEventsTrendResponse): EChartsOption {
    const isVariation = this.kpiComparisonMode === 'variation';
    const metricColor = trend.eventType === 'fall' ? '#ff5b5d' : '#2774ff';
    const categories = trend.series[0]?.points.map(point => this.formatManagedEventsBucket(
      point.bucketStart,
      trend.granularity,
      trend.period.timezone
    )) ?? [];
    const hasComparisons = trend.series.some(series => !series.isTotal);
    const labelStep = trend.granularity === 'hour'
      ? 4
      : categories.length > 12
        ? 5
        : 1;
    const lastCategoryIndex = Math.max(0, categories.length - 1);
    const seriesDefinitions = trend.series.map((series, index) => {
      const selectedIndex = Math.max(0, this.selectedKpiComparisonIds.indexOf(series.id));
      const color = series.isTotal
        ? (hasComparisons ? '#7b8798' : metricColor)
        : this.kpiComparisonColors[selectedIndex % this.kpiComparisonColors.length];
      return {
        name: series.label,
        isTotal: series.isTotal,
        color,
        data: series.points.map(point => isVariation
          ? (point.comparisonAvailable && point.percentageChange !== null
            ? Number(point.percentageChange)
            : null)
          : Number(point.value ?? 0))
      };
    });

    return {
      animationDuration: 350,
      color: seriesDefinitions.map(series => series.color),
      grid: { top: 18, right: 22, bottom: 36, left: 58, containLabel: false },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e2e7ef',
        borderWidth: 1,
        textStyle: { color: '#26334d', fontSize: 11 },
        valueFormatter: (value: any) => {
          const numericValue = Number(value);
          if (isVariation) {
            const sign = numericValue > 0 ? '+' : '';
            return sign + numericValue.toLocaleString('it-IT', { maximumFractionDigits: 2 }) + '%';
          }
          return numericValue.toLocaleString('it-IT');
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories,
        axisTick: {
          show: true,
          alignWithLabel: true,
          interval: 0,
          lineStyle: { color: '#dfe5ee' }
        },
        axisLine: { lineStyle: { color: '#dfe5ee' } },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          showMinLabel: true,
          showMaxLabel: true,
          interval: (index: number) =>
            index === 0 || index === lastCategoryIndex || index % labelStep === 0
        }
      },
      yAxis: {
        type: 'value',
        min: isVariation ? undefined : 0,
        minInterval: isVariation ? undefined : 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          formatter: (value: number) => isVariation
            ? `${value > 0 ? '+' : ''}${value.toLocaleString('it-IT', { maximumFractionDigits: 0 })}%`
            : value.toLocaleString('it-IT')
        },
        splitLine: { lineStyle: { color: '#edf0f5', type: 'dashed' } }
      },
      series: seriesDefinitions.map(series => ({
        name: series.name,
        type: 'line' as const,
        smooth: 0.2,
        connectNulls: false,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: categories.length > 12 ? 4 : 7,
        data: series.data,
        lineStyle: {
          width: series.isTotal && hasComparisons ? 2 : 2.5,
          color: series.color,
          type: series.isTotal && hasComparisons ? 'dashed' : 'solid'
        },
        itemStyle: { color: series.color, borderColor: '#fff', borderWidth: 1.5 },
        areaStyle: series.isTotal && !hasComparisons && !isVariation
          ? { color: trend.eventType === 'fall' ? 'rgba(255, 91, 93, .10)' : 'rgba(39, 116, 255, .10)' }
          : undefined,
        emphasis: { focus: 'series' as const }
      }))
    };
  }

  private buildResponseTimeTrendChartOptions(trend: IAnalyticsResponseTimeTrendResponse): EChartsOption {
    const isVariation = this.kpiComparisonMode === 'variation';
    const categories = trend.series[0]?.points.map(point => this.formatManagedEventsBucket(
      point.bucketStart,
      trend.granularity,
      trend.period.timezone
    )) ?? [];
    const hasComparisons = trend.series.some(series => !series.isTotal);
    const labelStep = trend.granularity === 'hour'
      ? 4
      : categories.length > 12
        ? 5
        : 1;
    const lastCategoryIndex = Math.max(0, categories.length - 1);
    const seriesDefinitions = trend.series.map(series => {
      const selectedIndex = Math.max(0, this.selectedKpiComparisonIds.indexOf(series.id));
      const color = series.isTotal
        ? (hasComparisons ? '#7b8798' : '#2774ff')
        : this.kpiComparisonColors[selectedIndex % this.kpiComparisonColors.length];

      return {
        name: series.label,
        isTotal: series.isTotal,
        color,
        data: series.points.map(point => isVariation
          ? (point.comparisonAvailable && point.percentageChange !== null
            ? Number(point.percentageChange)
            : null)
          : (point.medianResponseTimeMs === null ? null : Number(point.medianResponseTimeMs)))
      };
    });

    return {
      animationDuration: 350,
      color: seriesDefinitions.map(series => series.color),
      grid: { top: 18, right: 22, bottom: 36, left: 70, containLabel: false },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e2e7ef',
        borderWidth: 1,
        textStyle: { color: '#26334d', fontSize: 11 },
        valueFormatter: (value: any) => {
          if (value === null || value === undefined || value === '-') return 'N/D';
          const numericValue = Number(value);
          if (isVariation) {
            return `${numericValue > 0 ? '+' : ''}${numericValue.toLocaleString('it-IT', { maximumFractionDigits: 2 })}%`;
          }
          return this.formatDuration(numericValue / 1000);
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories,
        axisTick: {
          show: true,
          alignWithLabel: true,
          interval: 0,
          lineStyle: { color: '#dfe5ee' }
        },
        axisLine: { lineStyle: { color: '#dfe5ee' } },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          showMinLabel: true,
          showMaxLabel: true,
          interval: (index: number) =>
            index === 0 || index === lastCategoryIndex || index % labelStep === 0
        }
      },
      yAxis: {
        type: 'value',
        min: isVariation ? undefined : 0,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          formatter: (value: number) => isVariation
            ? `${value > 0 ? '+' : ''}${value.toLocaleString('it-IT', { maximumFractionDigits: 0 })}%`
            : this.formatDuration(value / 1000)
        },
        splitLine: { lineStyle: { color: '#edf0f5', type: 'dashed' } }
      },
      series: seriesDefinitions.map(series => ({
        name: series.name,
        type: 'line' as const,
        smooth: 0.2,
        connectNulls: false,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: categories.length > 12 ? 4 : 7,
        data: series.data,
        lineStyle: {
          width: series.isTotal && hasComparisons ? 2 : 2.5,
          color: series.color,
          type: series.isTotal && hasComparisons ? 'dashed' : 'solid'
        },
        itemStyle: { color: series.color, borderColor: '#fff', borderWidth: 1.5 },
        areaStyle: series.isTotal && !hasComparisons && !isVariation
          ? { color: 'rgba(39, 116, 255, .10)' }
          : undefined,
        emphasis: { focus: 'series' as const }
      }))
    };
  }

  private buildMockComparisonValues(
    type: AnalyticsKpiDetailType,
    totalValues: number[],
    option: AnalyticsKpiComparisonOption,
    optionIndex: number
  ): number[] {
    const shareByLevel: Record<AnalyticsKpiComparisonLevel, number> = {
      organization: 0.72,
      building: 0.56,
      unit: 0.46,
      room: 0.34,
      sensor: 0.24
    };
    const seed = option.id.split('').reduce((sum, character) => sum + character.charCodeAt(0), 0);
    const entityAdjustment = 0.88 + optionIndex * 0.08;

    return totalValues.map((total, pointIndex) => {
      const wave = 0.78
        + ((seed + pointIndex * 7) % 11) / 20
        + Math.sin((pointIndex + seed % 5) * 0.82) * 0.16;

      if (type === 'responseTime') {
        const responseAdjustment = 0.78 + (seed % 9) / 25;
        return Math.max(20, Math.round(
          total * responseAdjustment
          + Math.sin((pointIndex + optionIndex) * 0.9) * 24
          + optionIndex * 8
        ));
      }

      return Math.max(0, Math.round(
        total * shareByLevel[option.level] * entityAdjustment * wave
      ));
    });
  }

  private toPercentageVariation(values: number[]): number[] {
    const baseline = values.find(value => value !== 0) ?? 0;
    if (!baseline) return values.map(() => 0);

    return values.map(value =>
      Math.round(((value - baseline) / baseline) * 1000) / 10
    );
  }

  onFilterChange(widget: DashboardWidget, newValue: string) {
    if (newValue === widget.currentFilter) return;
    widget.currentFilter = newValue;
    widget.data = undefined;
    this.loadSingleWidgetData(widget);
    this.triggerAutoSave();
  }

  get heatmapCurrentFilter(): string {
    return this.getWidget('heatmap_hourly')?.currentFilter ?? 'all_alerts';
  }

  get heatmapFilterLabel(): string {
    return this.heatmapFilterOptions.find(option => option.value === this.heatmapCurrentFilter)?.label ?? 'Tutte le allerte';
  }

  private get heatmapStatus(): AnalyticsHourlyHeatmapStatus {
    const statuses: Record<string, AnalyticsHourlyHeatmapStatus> = {
      all_alerts: 'all',
      managed_alerts: 'operatorManaged',
      unmanaged_alerts: 'selfManaged'
    };
    return statuses[this.heatmapCurrentFilter] ?? 'all';
  }

  get newDashboardPeriodLabel(): string {
    return this.getDashboardPeriodOption(this.newDashboardPeriod).label;
  }

  get newDashboardPeriodContextLabel(): string {
    return this.getDashboardPeriodOption(this.newDashboardPeriod).contextLabel;
  }

  private getDashboardPeriodOption(period: AnalyticsPeriod) {
    return this.newDashboardPeriodOptions.find((option) => option.value === period)
      ?? this.newDashboardPeriodOptions[0];
  }

  private restoreDashboardPeriod(widgets: DashboardWidget[]): void {
    const savedPeriod = widgets.find((widget) => widget.id === 'kpi_alerts')?.currentFilter;
    if (!this.isAnalyticsPeriod(savedPeriod)) return;

    this.newDashboardPeriod = savedPeriod;
    this.synchronizedDashboardWidgetIds.forEach((widgetId) => {
      const widget = widgets.find((candidate) => candidate.id === widgetId);
      if (widget?.availableFilters?.includes(savedPeriod)) {
        widget.currentFilter = savedPeriod;
      }
    });
  }

  private isAnalyticsPeriod(value: string | undefined): value is AnalyticsPeriod {
    return this.newDashboardPeriodOptions.some((option) => option.value === value);
  }

  onHeatmapFilterChange(filter: string) {
    const widget = this.getWidget('heatmap_hourly');
    if (!widget) return;
    if (filter === widget.currentFilter) return;
    widget.currentFilter = filter;
    this.loadHeatmapDetails();
    this.triggerAutoSave();
  }

  toggleLegend(widget: DashboardWidget) {
    widget.showLegend = !widget.showLegend;
    this.triggerAutoSave();
  }

  toggleLabels(widget: DashboardWidget) {
    widget.showLabels = !widget.showLabels;
    this.triggerAutoSave();
  }

  onVisibilityChange() {
    this.triggerAutoSave();
  }

  public get availableDashboardCards(): AnalyticsDashboardCardPreference[] {
    return this.dashboardCards.filter(card => !card.superUserOnly || this.isSuperUser);
  }

  public isDashboardCardVisible(id: AnalyticsDashboardCardId): boolean {
    return this.dashboardCards.find(card => card.id === id)?.visible ?? true;
  }

  public onSensorsDetailsChange(open: boolean): void {
    this.sensorsDetailsOpen = open;
    if (open) this.closeKpiDetails();
  }

  public setDashboardCardVisibility(id: AnalyticsDashboardCardId, visible: boolean): void {
    const card = this.dashboardCards.find(candidate => candidate.id === id);
    if (!card || card.visible === visible) return;

    card.visible = visible;
    if (!visible && id === 'kpi_sensors') this.sensorsDetailsOpen = false;
    const detailCardIds: Partial<Record<AnalyticsDashboardCardId, AnalyticsKpiDetailType>> = {
      kpi_events: 'events',
      kpi_falls: 'falls',
      kpi_response_time: 'responseTime'
    };
    if (!visible && detailCardIds[id] === this.activeKpiDetail) {
      this.closeKpiDetails();
    }
    if (!visible && id === 'hourly_heatmap') {
      this.closeHeatmapDetails();
    }
    if (!visible && id === 'event_types') {
      this.closeEventTypesDetails();
    }
    if (!visible && id === 'managed_events_gauge') {
      this.closeManagedEventsDetails();
    }
    if (!visible && id === 'usage_adoption') {
      this.closeUsageUsersDetails();
    }
    if (!visible && id === 'usage_daily') {
      this.closeUsageDailyDetails();
    }
    this.triggerAutoSave();
  }

  public showAllDashboardCards(): void {
    let changed = false;
    this.availableDashboardCards.forEach(card => {
      if (!card.visible) {
        card.visible = true;
        changed = true;
      }
    });
    if (changed) this.triggerAutoSave();
  }

  private restoreDashboardCards(savedCards: Array<{ id: AnalyticsDashboardCardId; visible: boolean }> | undefined): void {
    if (!Array.isArray(savedCards)) return;
    this.dashboardCards.forEach(card => {
      const saved = savedCards.find(candidate => candidate.id === card.id);
      if (typeof saved?.visible === 'boolean') {
        card.visible = saved.visible;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSunburstOptions(widget: DashboardWidget): EChartsOption {
    const data = widget.data || [];

    return {
      animation: false,
      // Il Tooltip è fondamentale visto che non abbiamo etichette fisse
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          // Esempio: "Stanza 101: 15 eventi"
          return `${params.name}: <strong>${params.value}</strong> eventi`;
        }
      },
      series: {
        type: 'sunburst',
        data: data,
        radius: ['15%', '90%'], // Buco centrale leggermente più grande

        // --- 1. PULIZIA TOTALE ---
        label: {
          show: false, // Nasconde tutto di default: niente più accavallamenti!
        },
        nodeClick: false,

        // --- 2. INTERAZIONE ---
        emphasis: {
          focus: 'ancestor', // Evidenzia il percorso
          label: {
            show: true,
            formatter: '{b}\n{c}', // Mostra il nome
            fontSize: 14,
            fontWeight: 'bold',
            color: '#333', // Testo scuro per leggibilità (o #fff se sfondo scuro)
            textBorderColor: '#fff', // Bordo bianco per contrasto sopra i colori
            textBorderWidth: 2
          }
        },

        itemStyle: {
          borderRadius: 4,
          borderWidth: 1,
          borderColor: '#fff'
        },

        // Colori per livello (senza configurazioni label complicate)
        levels: [
          {}, // Livello 0
          { itemStyle: { color: '#5470c6' } }, // Livello 1 (Blu)
          { itemStyle: { color: '#91cc75' } }, // Livello 2 (Verde)
          { itemStyle: { color: '#fac858' } }, // Livello 3 (Giallo)
          { itemStyle: { color: '#ee6666' } }  // Livello 4 (Rosso - se c'è)
        ]
      }
    };
  }

  getEchartsPieOptions(widget: DashboardWidget): EChartsOption {
    const data = widget.data || [];
    const showLegend = widget.showLegend ?? false;
    const chartCenter = showLegend ? ['30%', '50%'] : ['50%', '50%'];
    return {
      animation: false,
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)' // Tooltip: Nome: Valore (Percentuale)
      },
      legend: {
        show: showLegend,
        type: 'scroll',
        orient: 'vertical',
        right: 0,
        top: 20,
        bottom: 10,
        width: 200, // Allargo leggermente per far starci i numeri
        pageIconColor: '#aaa',
        textStyle: {
          width: 150,
          overflow: 'truncate',
          fontSize: 12, // Aumento leggermente
          color: '#666'
        },
        // --- QUESTA È LA PARTE CHE AGGIUNGE I VALORI ---
        formatter: (name: string) => {
          // Cerco l'oggetto dati che corrisponde a questo nome in legenda
          const item = data.find((d: any) => d.name === name);

          // Se il nome è lunghissimo, lo taglio manualmente per sicurezza
          const cleanName = name.length > 12 ? name.substring(0, 12) + '..' : name;

          // Ritorno "Nome : Valore" (es. "Cadute : 12")
          return item ? `${cleanName} : ${item.value}` : name;
        }
        // -----------------------------------------------
      },
      series: [
        {
          name: widget.label,
          type: 'pie',
          radius: ['45%', '70%'],
          center: chartCenter,
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false, // Lasciamo false sul grafico per non fare confusione
            position: 'center'
          },
          data: data
        }
      ]
    };
  }

  getVMMetricsOptions(widget: any): any {
    if (!widget.data || !Array.isArray(widget.data)) return {};

    const rawData = widget.data;

    // 1. Estraiamo l'asse X (i nomi delle VM)
    const xAxisCategories = rawData.map((d: any) => d.vmName);

    // 2. Estraiamo i valori per le due serie (CPU e RAM)
    const cpuData = rawData.map((d: any) => {
      const metric = d.metrics.find((m: any) => m.metricName === 'Percentage CPU');
      return metric ? metric.averageValue : 0;
    });

    const ramData = rawData.map((d: any) => {
      const metric = d.metrics.find((m: any) => m.metricName === 'Available Memory');
      return metric ? metric.averageValue : 0;
    });

    // 3. Costruiamo le opzioni di ECharts con DOPPIO ASSE Y
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        bottom: 0 // Spostiamo la legenda in basso
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          data: xAxisCategories,
          axisLabel: {
            interval: 0,
            rotate: 30, // Ruotiamo i nomi delle VM se sono lunghi (es. 'vm-witacare-sfu-prod')
            fontSize: 10
          }
        }
      ],
      // ECCO IL DOPPIO ASSE: Index 0 (Sinistra, CPU) - Index 1 (Destra, RAM)
      yAxis: [
        {
          type: 'value',
          name: 'CPU',
          position: 'left',
          axisLabel: { formatter: '{value} %' },
          splitLine: { show: true, lineStyle: { type: 'dashed', color: '#eee' } }
        },
        {
          type: 'value',
          name: 'RAM Libera',
          position: 'right',
          axisLabel: { formatter: '{value} GB' },
          splitLine: { show: false } // Nascondiamo la griglia del secondo asse per non fare confusione
        }
      ],
      series: [
        {
          name: 'CPU (%)',
          type: 'bar',
          yAxisIndex: 0, // Si aggancia all'asse di sinistra
          data: cpuData,
          itemStyle: { color: '#5470C6', borderRadius: [4, 4, 0, 0] },
          barMaxWidth: 35
        },
        {
          name: 'RAM Libera (GB)',
          type: 'bar',
          yAxisIndex: 1, // Si aggancia all'asse di destra
          data: ramData,
          itemStyle: { color: '#91CC75', borderRadius: [4, 4, 0, 0] },
          barMaxWidth: 35
        }
      ]
    };
  }

  // --- GETTERS & UTILS ---

  get isSuperUser(): boolean {
    return this.currentUser?.role === 'superuser';
  }

  get activeWidgets() {
    return this.widgetRegistry
      .filter(w => w.visible)
      .sort((a, b) => {
        if (a.pinned === b.pinned) return 0;
        return a.pinned ? -1 : 1;
      });
  }

  getWidget(widgetId: string): DashboardWidget | undefined {
    return this.widgetRegistry.find(widget => widget.id === widgetId);
  }

  get sensorsData(): IAnalyticsOnlineSensors {
    return this.getWidget('kpi_sensors')?.data ?? { online: 0, total: 0 };
  }

  get eventsTotal(): number {
    return Number(this.eventsSummary?.events.currentValue ?? 0);
  }

  get medianResponseTime(): string {
    const value = this.eventsSummary?.medianResponseTime?.currentValueMs;
    return value === null || value === undefined
      ? 'N/A'
      : this.formatResponseTimeMs(value);
  }

  get fallsTotal(): number {
    return Number(this.eventsSummary?.falls.currentValue ?? 0);
  }

  get onlineSensorsPercentage(): number {
    if (!this.sensorsData.total) return 0;
    return Math.round((this.sensorsData.online / this.sensorsData.total) * 100);
  }

  get alertTypeItems(): Array<{ key: string; label: string; value: number; color: string; percentage: number }> {
    const rawData: IAnalyticsAlertTypes[] = this.getWidget('alert_types')?.data ?? [];
    return this.mapAlertTypeItems(rawData);
  }

  get weeklyChartAlertTypeItems(): Array<{ key: string; label: string; value: number; color: string; percentage: number }> {
    return this.mapAlertTypeItems(this.weeklyAlertTypesData);
  }

  get managedEventsGaugeData(): { managed: number; total: number; percentage: number } {
    const values = this.operationsSummary?.managedEvents;

    return {
      managed: Number(values?.operatorManaged ?? 0),
      total: Number(values?.total ?? 0),
      percentage: Math.round(Number(values?.operatorManagedPercentage ?? 0))
    };
  }

  public kpiPeriodComparison(type: 'events' | 'falls' | 'responseTime'): { value: number } | undefined {
    if (type === 'events' || type === 'falls') {
      const comparison = this.eventsSummary?.[type];
      if (!comparison?.comparisonAvailable || comparison.percentageChange === null) return undefined;
      return { value: Math.round(Number(comparison.percentageChange) * 10) / 10 };
    }

    const comparison = this.eventsSummary?.medianResponseTime;
    if (!comparison?.comparisonAvailable || comparison.percentageChange === null) return undefined;
    return { value: Math.round(Number(comparison.percentageChange) * 10) / 10 };
  }

  private formatResponseTimeMs(valueMs: number): string {
    const totalSeconds = Math.max(0, Math.round(Number(valueMs) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  }

  private refreshModernChartOptions() {
    this.weeklyEventsOptions = this.buildWeeklyEventsOptions();
    this.modernAlertTypesOptions = this.buildModernAlertTypesOptions();
    this.managedEventsGaugeOptions = this.buildManagedEventsGaugeOptions();
  }

  private buildManagedEventsGaugeOptions(): EChartsOption {
    const data = this.managedEventsGaugeData;

    return {
      animationDuration: 450,
      series: [
        {
          type: 'gauge',
          startAngle: 205,
          endAngle: -25,
          min: 0,
          max: 100,
          radius: '90%',
          center: ['50%', '57%'],
          progress: {
            show: true,
            roundCap: true,
            width: 18,
            itemStyle: { color: '#2774ff' }
          },
          axisLine: {
            roundCap: true,
            lineStyle: {
              width: 18,
              color: [[1, '#e8eef7']]
            }
          },
          pointer: { show: false },
          anchor: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          title: { show: false },
          detail: {
            valueAnimation: true,
            offsetCenter: [0, '0%'],
            color: '#17213b',
            fontSize: 34,
            fontWeight: 750,
            formatter: '{value}%'
          },
          data: [{ value: data.percentage }]
        }
      ]
    };
  }

  private rebuildManagedEventsDetailsChart(): void {
    if (!this.managedEventsDetailsOpen || !this.managedEventsTrend) {
      this.managedEventsDetailsChartOptions = {};
      return;
    }
    this.managedEventsDetailsChartOptions = this.buildManagedEventsDetailsChartOptions();
  }

  private buildManagedEventsDetailsChartOptions(): EChartsOption {
    const trend = this.managedEventsTrend;
    if (!trend) return {};

    const categories = trend.points.map(point => this.formatManagedEventsBucket(
      point.bucketStart,
      trend.granularity,
      trend.period.timezone
    ));
    const managed = trend.points.map(point => Number(point.operatorManaged ?? 0));
    const selfManaged = trend.points.map(point => Number(point.selfManaged ?? 0));
    const totals = trend.points.map(point => Number(point.total ?? 0));
    const labelStep = this.newDashboardPeriod === '24h'
      ? 4
      : this.newDashboardPeriod === '30d'
        ? 5
        : 1;
    const lastCategoryIndex = Math.max(0, categories.length - 1);

    return {
      animationDuration: 350,
      color: ['#2774ff', '#b9c9df', '#65758b'],
      grid: { top: 48, right: 24, bottom: 38, left: 52, containLabel: false },
      legend: {
        top: 0,
        left: 0,
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: '#667085', fontSize: 11 }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: '#ffffff',
        borderColor: '#e2e7ef',
        borderWidth: 1,
        textStyle: { color: '#26334d', fontSize: 11 }
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#dfe5ee' } },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          showMinLabel: true,
          showMaxLabel: true,
          interval: (index: number) =>
            index === 0 || index === lastCategoryIndex || index % labelStep === 0
        }
      },
      yAxis: {
        type: 'value',
        min: 0,
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#78859a', fontSize: 10 },
        splitLine: { lineStyle: { color: '#edf0f5', type: 'dashed' } }
      },
      series: [
        {
          name: 'Gestite dagli operatori',
          type: 'bar',
          stack: 'segnalazioni',
          barMaxWidth: 30,
          data: managed,
          itemStyle: { color: '#2774ff', borderRadius: [0, 0, 3, 3] }
        },
        {
          name: 'Autogestite',
          type: 'bar',
          stack: 'segnalazioni',
          barMaxWidth: 30,
          data: selfManaged,
          itemStyle: { color: '#b9c9df', borderRadius: [3, 3, 0, 0] }
        },
        {
          name: 'Totale segnalazioni',
          type: 'line',
          smooth: 0.2,
          symbol: 'circle',
          symbolSize: categories.length > 12 ? 4 : 6,
          data: totals,
          lineStyle: { width: 2, type: 'dashed', color: '#65758b' },
          itemStyle: { color: '#65758b' },
          z: 3
        }
      ]
    };
  }

  private formatManagedEventsBucket(
    bucketStart: string,
    granularity: IAnalyticsManagedEventsTrendResponse['granularity'],
    timeZone: string
  ): string {
    const date = new Date(bucketStart);
    if (Number.isNaN(date.getTime())) return bucketStart;

    if (granularity === 'hour') {
      return new Intl.DateTimeFormat('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone
      }).format(date);
    }

    if (granularity === 'month') {
      return new Intl.DateTimeFormat('it-IT', {
        month: 'short',
        timeZone
      }).format(date).replace('.', '');
    }

    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: 'short',
      timeZone
    }).format(date).replace('.', '');
  }

  private rebuildEventTypesDetailsChart(): void {
    if (!this.eventTypesDetailsOpen) {
      this.eventTypesDetailsChartOptions = {};
      return;
    }
    this.eventTypesDetailsChartOptions = this.buildEventTypesDetailsChartOptions();
  }

  private buildEventTypesDetailsChartOptions(): EChartsOption {
    const categories = this.buildEventTrendBuckets([]).map(bucket => bucket.label);
    const isVariation = this.eventTypesComparisonMode === 'variation';
    const scaleByPeriod: Record<AnalyticsPeriod, number> = {
      '24h': 2,
      '7d': 5,
      '30d': 7,
      '1y': 85
    };
    const xAxisLabelStep = this.newDashboardPeriod === '24h'
      ? 4
      : this.newDashboardPeriod === '30d'
        ? 5
        : 1;
    const lastCategoryIndex = Math.max(0, categories.length - 1);
    const definitions = [
      { name: 'Ingresso letto', color: '#22c6c8', weight: 1.0, seed: 2 },
      { name: 'Uscita letto', color: '#3b82f6', weight: 1.12, seed: 3 },
      { name: 'Ingresso stanza', color: '#f5a623', weight: 0.64, seed: 5 },
      { name: 'Uscita stanza', color: '#76bf43', weight: 0.78, seed: 7 },
      { name: 'Dispositivo offline', color: '#8b5cf6', weight: 0.42, seed: 11 },
      { name: 'Caduta', color: '#ff5b5d', weight: 0.24, seed: 13 }
    ].map(definition => {
      const values = categories.map((_, index) => {
        const wave = 0.55
          + ((index * definition.seed + definition.seed) % 7) / 7
          + Math.sin((index + definition.seed) * 0.72) * 0.16;
        return Math.max(0, Math.round(scaleByPeriod[this.newDashboardPeriod] * definition.weight * wave));
      });

      return {
        ...definition,
        values: isVariation ? this.toPercentageVariation(values) : values
      };
    });

    return {
      animationDuration: 350,
      color: definitions.map(definition => definition.color),
      grid: { top: 52, right: 22, bottom: 36, left: 52, containLabel: false },
      legend: {
        type: 'scroll',
        top: 0,
        left: 0,
        right: 0,
        itemWidth: 9,
        itemHeight: 9,
        icon: 'circle',
        textStyle: { color: '#667085', fontSize: 10 }
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e2e7ef',
        borderWidth: 1,
        textStyle: { color: '#26334d', fontSize: 11 },
        valueFormatter: (value: any) => {
          const numericValue = Number(value);
          if (!isVariation) return numericValue.toLocaleString('it-IT');
          const sign = numericValue > 0 ? '+' : '';
          return sign + numericValue.toLocaleString('it-IT', { maximumFractionDigits: 1 }) + '%';
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories,
        axisTick: { show: true, alignWithLabel: true, interval: 0, lineStyle: { color: '#dfe5ee' } },
        axisLine: { lineStyle: { color: '#dfe5ee' } },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          showMinLabel: true,
          showMaxLabel: true,
          interval: (index: number) =>
            index === 0 || index === lastCategoryIndex || index % xAxisLabelStep === 0
        }
      },
      yAxis: {
        type: 'value',
        min: isVariation ? undefined : 0,
        minInterval: isVariation ? undefined : 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#78859a',
          fontSize: 10,
          formatter: (value: number) => {
            if (!isVariation) return value.toLocaleString('it-IT');
            const sign = value > 0 ? '+' : '';
            return sign + value.toLocaleString('it-IT', { maximumFractionDigits: 0 }) + '%';
          }
        },
        splitLine: { lineStyle: { color: '#edf0f5', type: 'dashed' } }
      },
      series: definitions.map(definition => ({
        name: definition.name,
        type: 'line' as const,
        smooth: 0.2,
        connectNulls: true,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: categories.length > 12 ? 4 : 6,
        data: definition.values,
        lineStyle: { width: 2.25, color: definition.color },
        itemStyle: { color: definition.color, borderColor: '#fff', borderWidth: 1.25 },
        emphasis: { focus: 'series' as const }
      }))
    };
  }
  private buildUsageAdoptionGaugeOptions(): EChartsOption {
    const percentage = Math.max(0, Math.min(100, Number(this.usageSummary?.adoptionPercentage ?? 0)));
    return {
      animationDuration: 500,
      series: [{
        type: 'gauge',
        startAngle: 205,
        endAngle: -25,
        min: 0,
        max: 100,
        radius: '92%',
        center: ['50%', '58%'],
        pointer: { show: false },
        progress: {
          show: true,
          roundCap: true,
          width: 14,
          itemStyle: { color: '#2774ff' }
        },
        axisLine: {
          roundCap: true,
          lineStyle: { width: 14, color: [[1, '#edf2f8']] }
        },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        anchor: { show: false },
        title: { show: false },
        detail: {
          valueAnimation: true,
          offsetCenter: [0, '4%'],
          color: '#17213b',
          fontSize: 28,
          fontWeight: 750,
          formatter: (value: number) => value.toLocaleString('it-IT', { maximumFractionDigits: 1 }) + '%'
        },
        data: [{ value: percentage }]
      }]
    };
  }

  private buildUsageUsersPreviewOptions(): EChartsOption {
    const users = [...(this.usageUsersPreview?.users ?? [])].reverse();
    return {
      animationDuration: 450,
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: 'rgba(20, 31, 52, 0.94)',
        borderWidth: 0,
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any) =>
          '<strong>' + params.name + '</strong><br/>Tempo unico: ' +
          this.formatUsageDuration(params.data.durationMs)
      },
      grid: { top: 8, left: 105, right: 24, bottom: 30 },
      xAxis: {
        type: 'value',
        min: 0,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#7f899b',
          fontSize: 10,
          formatter: (value: number) => value.toLocaleString('it-IT', { maximumFractionDigits: 1 }) + ' h'
        },
        splitLine: { lineStyle: { color: '#edf0f5', type: 'dashed' } }
      },
      yAxis: {
        type: 'category',
        data: users.map(user => user.displayName),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#526078',
          fontSize: 11,
          width: 92,
          overflow: 'truncate'
        }
      },
      series: [{
        name: 'Tempo unico',
        type: 'bar',
        barMaxWidth: 18,
        itemStyle: { color: '#2774ff', borderRadius: [0, 5, 5, 0] },
        data: users.map(user => ({
          value: this.durationMsToHours(user.uniqueDurationMs),
          durationMs: user.uniqueDurationMs
        }))
      }]
    };
  }

  private buildUsageDailyChartOptions(): EChartsOption {
    const daily = this.usageDaily;
    const points = daily?.points ?? [];
    const timezone = daily?.period.timezone ?? 'Europe/Rome';
    const categories = points.map(point => this.formatUsageBucket(point.bucketStart, daily?.granularity ?? 'day', timezone));
    const labelStep = points.length > 20 ? Math.max(1, Math.floor(points.length / 6)) : 0;

    return {
      animationDuration: 450,
      color: ['#2774ff', '#8b5cf6'],
      tooltip: {
        trigger: 'axis',
        confine: true,
        backgroundColor: 'rgba(20, 31, 52, 0.94)',
        borderWidth: 0,
        padding: [8, 10],
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          const title = items[0]?.axisValueLabel ?? '';
          return '<strong>' + title + '</strong><br/>' + items.map((item: any) =>
            item.marker + item.seriesName + ': ' + this.formatUsageDuration(item.data.durationMs)
          ).join('<br/>');
        }
      },
      legend: {
        top: 2,
        left: 0,
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 18,
        textStyle: { color: '#526078', fontSize: 11 }
      },
      grid: { top: 48, left: 48, right: 18, bottom: 34 },
      xAxis: {
        type: 'category',
        data: categories,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#e7eaf0' } },
        axisLabel: {
          color: '#69758b',
          fontSize: 10,
          interval: labelStep ? (index: number) => index === 0 || index === points.length - 1 || index % labelStep === 0 : 0
        }
      },
      yAxis: {
        type: 'value',
        min: 0,
        axisLabel: {
          color: '#7f899b',
          fontSize: 10,
          formatter: (value: number) => value.toLocaleString('it-IT', { maximumFractionDigits: 1 }) + ' h'
        },
        splitLine: { lineStyle: { color: '#edf0f5', type: 'dashed' } }
      },
      series: [
        {
          name: 'Utilizzo applicazione',
          type: 'bar',
          barMaxWidth: 30,
          itemStyle: { color: '#2774ff', borderRadius: [4, 4, 0, 0] },
          data: points.map(point => ({
            value: this.durationMsToHours(point.applicationUniqueDurationMs),
            durationMs: point.applicationUniqueDurationMs
          }))
        },
        {
          name: 'Streaming',
          type: 'line',
          smooth: 0.2,
          showSymbol: points.length <= 12,
          symbolSize: 6,
          lineStyle: { width: 2.5, color: '#8b5cf6' },
          itemStyle: { color: '#8b5cf6' },
          data: points.map(point => ({
            value: this.durationMsToHours(point.streamingUniqueDurationMs),
            durationMs: point.streamingUniqueDurationMs
          }))
        }
      ]
    };
  }

  private durationMsToHours(value: string | number): number {
    const duration = Number(value);
    return Number.isFinite(duration) ? Math.round((duration / 3_600_000) * 1000) / 1000 : 0;
  }

  public formatUsageDuration(value: string | number | null | undefined): string {
    const durationMs = Math.max(0, Number(value ?? 0));
    if (!Number.isFinite(durationMs) || durationMs === 0) return '0 min';

    const totalSeconds = Math.round(durationMs / 1000);
    if (totalSeconds < 60) return totalSeconds + ' sec';

    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);
    if (!hours) return totalMinutes + ' min';
    return hours + ' h' + (minutes ? ' ' + minutes + ' min' : '');
  }

  private formatUsageBucket(
    value: string,
    granularity: 'hour' | 'day' | 'month',
    timezone: string
  ): string {
    const options: Intl.DateTimeFormatOptions = granularity === 'hour'
      ? { hour: '2-digit', minute: '2-digit', timeZone: timezone }
      : granularity === 'month'
        ? { month: 'short', timeZone: timezone }
        : { day: '2-digit', month: 'short', timeZone: timezone };
    return this.capitalizeHeatmapLabel(new Intl.DateTimeFormat('it-IT', options).format(new Date(value)));
  }

  public get usageHeatmapRows(): Array<{
    weekday: number;
    label: string;
    cells: Array<{ hour: number; durationMs: string; activeUsers: number }>;
  }> {
    const labels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
    const rows = new Map((this.usageHeatmap?.rows ?? []).map(row => [row.weekday, row]));
    return labels.map((label, index) => {
      const weekday = index + 1;
      const cells = new Map((rows.get(weekday)?.cells ?? []).map(cell => [cell.startHour, cell]));
      return {
        weekday,
        label,
        cells: Array.from({ length: 24 }, (_, hour) => {
          const cell = cells.get(hour);
          return {
            hour,
            durationMs: cell?.uniqueDurationMs ?? '0',
            activeUsers: cell?.activeUsers ?? 0
          };
        })
      };
    });
  }

  public get usageHeatmapHours(): string[] {
    return Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
  }

  public usageHeatmapCellColor(durationMs: string): string {
    const value = Math.max(0, Number(durationMs) || 0);
    const intensity = value ? Math.max(0.14, value / this.usageHeatmapMaxDurationMs) : 0.06;
    const rgb = this.usageHeatmapKind === 'streaming_view' ? '139, 92, 246' : '39, 116, 255';
    return 'rgba(' + rgb + ', ' + Math.min(1, intensity) + ')';
  }

  private buildWeeklyEventsOptions(): EChartsOption {
    const rawData: any[] = this.newDashboardPeriod === '7d'
      ? this.getWidget('chart_weekly')?.data ?? []
      : [];
    const buckets = this.buildEventTrendBuckets(rawData);
    const categories = buckets.map(bucket => bucket.label);
    const dayTotals = buckets.map(bucket => bucket.value);
    const typeItems = this.weeklyChartAlertTypeItems;
    const lastCategoryIndex = Math.max(0, categories.length - 1);
    const categoryLabelStep = this.newDashboardPeriod === '24h'
      ? 4
      : this.newDashboardPeriod === '30d'
        ? 5
        : 1;
    let series: any[] = [];

    if (typeItems.length) {
      const totalTypeValue = typeItems.reduce((sum, item) => sum + item.value, 0);
      const distribution = dayTotals.map((dayTotal, dayIndex) => {
        const exactValues = typeItems.map(item => totalTypeValue ? (dayTotal * item.value) / totalTypeValue : 0);
        const values = exactValues.map(value => Math.floor(value));
        let remainder = Math.max(0, dayTotal - values.reduce((sum, value) => sum + value, 0));
        const rankedFractions = exactValues
          .map((value, index) => ({
            index,
            fraction: value - Math.floor(value),
            rotation: (index + dayIndex) % typeItems.length
          }))
          .sort((a, b) => b.fraction - a.fraction || a.rotation - b.rotation);

        for (let index = 0; index < remainder; index++) {
          values[rankedFractions[index % rankedFractions.length].index]++;
        }

        return values;
      });

      series = typeItems.map((item, typeIndex) => ({
        name: item.label,
        type: 'bar',
        stack: 'events',
        barMaxWidth: 38,
        itemStyle: {
          color: item.color,
          borderRadius: 3,
          borderColor: '#fff',
          borderWidth: 1
        },
        data: distribution.map(day => day[typeIndex])
      }));
    } else {
      series = [{
        name: 'Eventi rilevati',
        type: 'bar',
        barMaxWidth: 38,
        itemStyle: {
          color: '#3b82f6',
          borderRadius: 3,
          borderColor: '#fff',
          borderWidth: 1
        },
        data: dayTotals
      }];
    }

    return {
      animationDuration: 450,
      color: typeItems.map(item => item.color),
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: 'rgba(20, 31, 52, 0.94)',
        borderWidth: 0,
        padding: [8, 10],
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any) =>
          params.marker + params.seriesName + '<br/><strong>' +
          params.name + ': ' + params.value + '</strong> eventi'
      },
      legend: {
        top: 2,
        left: 0,
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 18,
        textStyle: { color: '#526078', fontSize: 12 }
      },
      grid: { top: 50, left: 42, right: 14, bottom: 28 },
      xAxis: {
        type: 'category',
        data: categories,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#e7eaf0' } },
        axisLabel: {
          color: '#69758b',
          fontSize: 12,
          showMinLabel: true,
          showMaxLabel: true,
          interval: (index: number) =>
            index === 0 || index === lastCategoryIndex || index % categoryLabelStep === 0
        }
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: { color: '#7f899b', fontSize: 12 },
        splitLine: { lineStyle: { color: '#e9edf3' } }
      },
      series
    };
  }

  private buildEventTrendBuckets(rawData: any[]): Array<{ label: string; value: number }> {
    const now = new Date();
    const weeklyTotals = new Map<string, number>();

    rawData.forEach(item => {
      const day = this.normalizeHeatmapDay(item?.name);
      if (day) weeklyTotals.set(day, Math.max(0, Math.round(Number(item?.value ?? 0))));
    });

    if (this.newDashboardPeriod === '24h') {
      return Array.from({ length: 24 }, (_, index) => {
        const date = new Date(now);
        date.setMinutes(0, 0, 0);
        date.setHours(date.getHours() - (23 - index));
        return {
          label: String(date.getHours()).padStart(2, '0') + ':00',
          value: 0
        };
      });
    }

    if (this.newDashboardPeriod === '7d') {
      const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      return Array.from({ length: 7 }, (_, index) => {
        const date = new Date(now);
        date.setHours(12, 0, 0, 0);
        date.setDate(date.getDate() - (6 - index));
        const day = dayKeys[date.getDay()];
        return {
          label: this.formatDayLabel(day),
          value: weeklyTotals.get(day) ?? 0
        };
      });
    }

    if (this.newDashboardPeriod === '30d') {
      return Array.from({ length: 30 }, (_, index) => {
        const date = new Date(now);
        date.setHours(12, 0, 0, 0);
        date.setDate(date.getDate() - (29 - index));
        return {
          label: this.capitalizeHeatmapLabel(
            new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short' }).format(date)
          ),
          value: 0
        };
      });
    }

    return Array.from({ length: 12 }, (_, index) => {
      const date = new Date(now);
      date.setHours(12, 0, 0, 0);
      date.setDate(1);
      date.setMonth(date.getMonth() - (11 - index));
      return {
        label: this.capitalizeHeatmapLabel(
          new Intl.DateTimeFormat('it-IT', { month: 'short' }).format(date)
        ),
        value: 0
      };
    });
  }

  private buildModernAlertTypesOptions(): EChartsOption {
    return {
      animationDuration: 450,
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: 'rgba(20, 31, 52, 0.94)',
        borderWidth: 0,
        padding: [8, 10],
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: '{b}: <strong>{c}</strong> ({d}%)'
      },
      series: [{
        type: 'pie',
        radius: ['57%', '79%'],
        center: ['50%', '50%'],
        minAngle: 3,
        label: { show: false },
        selectedMode: false,
        emphasis: { scale: false, focus: 'none' },
        itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
        data: this.alertTypeItems.map(item => ({
          name: item.label,
          value: item.value,
          itemStyle: { color: item.color, borderRadius: 5, borderColor: '#fff', borderWidth: 2 }
        }))
      }]
    };
  }

  get heatmapSummarySlots(): Array<{
    label: string;
    value: number;
    percentage: number;
    mostFrequent: boolean;
  }> {
    const summary = this.operationsSummary?.alertsByTimeSlot;
    const buckets = summary?.buckets ?? [];
    const values = buckets.map(bucket => Number(bucket.count ?? 0));
    const maxValue = Math.max(0, ...values);
    const mostFrequent = new Set(
      (summary?.mostFrequentBuckets ?? []).map(bucket => `${bucket.startHour}-${bucket.endHour}`)
    );

    return buckets.map((bucket, index) => ({
      label: `${String(bucket.startHour).padStart(2, '0')}-${String(bucket.endHour).padStart(2, '0')}`,
      value: values[index],
      percentage: maxValue ? Math.round((values[index] / maxValue) * 100) : 0,
      mostFrequent: mostFrequent.has(`${bucket.startHour}-${bucket.endHour}`)
    }));
  }
  get heatmapRows(): Array<{ day: string; label: string; values: Array<{ hour: string; value: number }> }> {
    const dayDefinitions = [
      { weekday: 1, day: 'mon', label: 'Lun' },
      { weekday: 2, day: 'tue', label: 'Mar' },
      { weekday: 3, day: 'wed', label: 'Mer' },
      { weekday: 4, day: 'thu', label: 'Gio' },
      { weekday: 5, day: 'fri', label: 'Ven' },
      { weekday: 6, day: 'sat', label: 'Sab' },
      { weekday: 7, day: 'sun', label: 'Dom' }
    ];
    const rowsByWeekday = new Map(
      (this.hourlyHeatmapDetails?.rows ?? []).map(row => [Number(row.weekday), row])
    );

    return dayDefinitions.map(definition => {
      const row = rowsByWeekday.get(definition.weekday);
      const cellsByHour = new Map(
        (row?.cells ?? []).map(cell => [Number(cell.startHour), Number(cell.count ?? 0)])
      );

      return {
        day: definition.day,
        label: definition.label,
        values: this.heatmapHours.map((hour, index) => ({
          hour,
          value: cellsByHour.get(index * 2) ?? 0
        }))
      };
    });
  }
  get heatmapHours(): string[] {
    return Array.from({ length: 12 }, (_, slotIndex) => {
      const start = slotIndex * 2;
      const end = (start + 2) % 24;
      return String(start).padStart(2, '0') + '-' + String(end).padStart(2, '0');
    });
  }

  private capitalizeHeatmapLabel(label: string): string {
    const normalized = label.replace('.', '');
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  private normalizeHeatmapDay(value: unknown): string | undefined {
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const aliases: Record<string, string> = {
      mon: 'mon', lun: 'mon', lunedi: 'mon',
      tue: 'tue', mar: 'tue', martedi: 'tue',
      wed: 'wed', mer: 'wed', mercoledi: 'wed',
      thu: 'thu', gio: 'thu', giovedi: 'thu',
      fri: 'fri', ven: 'fri', venerdi: 'fri',
      sat: 'sat', sab: 'sat', sabato: 'sat',
      sun: 'sun', dom: 'sun', domenica: 'sun'
    };

    return aliases[normalized];
  }

  get heatmapMaxValue(): number {
    const values = this.heatmapRows.flatMap(row => row.values.map(item => item.value));
    return Math.max(1, ...values);
  }

  heatmapCellColor(value: number): string {
    const intensity = Math.max(0.08, value / this.heatmapMaxValue);
    return 'rgba(37, 116, 255, ' + Math.min(1, intensity) + ')';
  }

  private mapAlertTypeItems(rawData: IAnalyticsAlertTypes[]): Array<{ key: string; label: string; value: number; color: string; percentage: number }> {
    const grouped = new Map<string, { key: string; label: string; value: number; color: string }>();

    rawData.forEach(item => {
      const rawName = String(item.name ?? '');
      const key = this.normalizeAlertTypeKey(rawName);

      if (!this.supportedAlertTypeKeys.has(key)) {
        return;
      }

      const current = grouped.get(key);
      const value = Number(item.value || 0);

      grouped.set(key, {
        key,
        label: this.getAlertTypeLabel(key),
        value: (current?.value ?? 0) + value,
        color: this.modernAlertPalette[key] ?? '#94a3b8'
      });
    });

    const items = [...grouped.values()].filter(item => item.value > 0);
    const total = items.reduce((sum, item) => sum + item.value, 0);

    return items
      .map(item => ({
        ...item,
        percentage: total ? Math.round((item.value / total) * 100) : 0
      }))
      .sort((a, b) => b.value - a.value);
  }

  private normalizeAlertTypeKey(type: string): string {
    const normalized = type
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');

    if (normalized === 'bed out' || normalized.includes('uscita letto')) return 'bed_out';
    if (normalized === 'bed in' || normalized === 'a letto' || normalized.includes('ingresso letto')) return 'bed_in';
    if (normalized === 'room out' || normalized.includes('uscita stanza')) return 'room_out';
    if (normalized === 'room in' || normalized.includes('ingresso stanza')) return 'room_in';
    if (normalized === 'fall' || normalized.includes('caduta')) return 'fall';
    if (normalized === 'device offline' || normalized.includes('dispositivo offline') || normalized.includes('sensore offline')) return 'device_offline';
    if (normalized === 'device online' || normalized.includes('dispositivo online') || normalized.includes('sensore online')) return 'device_online';
    if (normalized === '08' || normalized === 'codice 08') return 'test_notification';

    return normalized.replace(/\s+/g, '_') || 'other';
  }

  private getAlertTypeLabel(type: string): string {
    const key = this.normalizeAlertTypeKey(type);
    const labels: Record<string, string> = {
      bed_out: 'Uscita letto',
      bed_in: 'Ingresso letto',
      room_out: 'Uscita stanza',
      room_in: 'Ingresso stanza',
      fall: 'Caduta',
      device_offline: 'Dispositivo offline',
      device_online: 'Dispositivo online',
      test_notification: 'Notifica di test',
      other: 'Altri eventi'
    };

    if (labels[key]) return labels[key];

    const fallback = type.replace(/_/g, ' ').trim();
    return fallback ? fallback.charAt(0).toUpperCase() + fallback.slice(1) : 'Altro';
  }

  private formatDayLabel(day: string): string {
    const labels: Record<string, string> = {
      mon: 'Lun',
      tue: 'Mar',
      wed: 'Mer',
      thu: 'Gio',
      fri: 'Ven',
      sat: 'Sab',
      sun: 'Dom'
    };
    return labels[day] ?? day;
  }

  get groupedWidgets() {
    const sections = [...new Set(this.widgetRegistry.map(w => w.section))];
    return sections.map(sectionName => ({
      name: this.sectionToName[sectionName],
      widgets: this.widgetRegistry.filter(w => w.section === sectionName)
    }));
  }

  public filterMap: Record<string, string> = {
    '1h': 'Ultima ora',
    '24h': 'Ultime 24 Ore',
    '7d': 'Ultima Settimana',
    '30d': 'Ultimi 30 giorni',
    '1y': 'Ultimi 12 mesi',
    'all_alerts': 'Tutte le Allerte',
    'managed_alerts': 'Allerte Gestite',
    'unmanaged_alerts': 'Allerte Non Gestite',
  };

  public sectionToName: Record<string, string> = {
    'sensors': 'Sensori',
    'alerts': 'Allerte'
  };

  exportData(widget: DashboardWidget) {
    if (!widget.data || widget.data.length === 0) {
      this.toastService.showToast('Nessun dato da esportare', IonicColors.RED);
      return;
    }
    const data = widget.data;
    let csvData: any[] = [];

    if (Array.isArray(data)) {
      if (data.length > 0 && data[0].hasOwnProperty('series')) {
        data.forEach((group: any) => {
          if (Array.isArray(group.series)) {
            group.series.forEach((item: any) => {
              csvData.push({
                Gruppo: group.name,
                Etichetta: item.name,
                Valore: item.value
              });
            });
          }
        });
      } else {
        csvData = data;
      }
    } else if (typeof data === 'object') {
      csvData = [data];
    } else {
      csvData = [{ valore: data }];
    }

    if (csvData.length === 0) return;

    const headers = Object.keys(csvData[0]);
    const separator = ',';
    const csvRows = [];
    csvRows.push(headers.join(separator));
    for (const row of csvData) {
      const values = headers.map(header => {
        const escaped = ('' + row[header]).replace(/"/g, '\\"');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(separator));
    }
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `${widget.label.replace(/\s+/g, '_').toLowerCase()}_${new Date().getTime()}.csv`;
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // --- DEFINIZIONE WIDGET ---
  private getDefaultWidgets(): DashboardWidget[] {
    const widgets: DashboardWidget[] = [
      // --- KPI ---
      {
        id: 'kpi_sensors',
        widgetType: 'simple_on',
        label: 'Sensori Online',
        icon: 'wifi',
        visible: true,
        cols: 4, rows: 2, pinned: false,
        section: 'sensors',
        loading: true,
        dataLoader: (filter, context) => this.analyticsService.getKpiSensors$(context),
        data: null,
        isGlobalOnly: false
      },
      {
        id: 'kpi_alerts',
        widgetType: 'simple',
        label: 'Allerte',
        icon: 'alert-outline',
        availableFilters: ['24h', '7d', '30d', '1y'],
        currentFilter: '24h',
        visible: true,
        cols: 4, rows: 2, pinned: false,
        section: 'alerts',
        loading: true,
        dataLoader: (filter, context) => this.analyticsService.getKpiAlerts$(filter, context),
        data: null,
        isGlobalOnly: false
      },
      {
        id: 'kpi_response_time',
        widgetType: 'simple',
        label: 'Tempo mediano di presa in carico',
        icon: 'timer-outline',
        availableFilters: ['24h', '7d', '30d', '1y'],
        currentFilter: '24h',
        visible: true,
        cols: 4, rows: 2, pinned: false,
        section: 'alerts',
        loading: true,
        dataLoader: (filter, context) => this.analyticsService.getKpiResponseTime$(filter, context),
        data: null,
        isGlobalOnly: false
      },
      // --- GRAFICI MEDI ---
      {
        id: 'chart_weekly',
        widgetType: 'vertical_bars_chart',
        label: 'Andamento Settimanale',
        icon: 'stats-chart',
        visible: true,
        cols: 6, rows: 3, pinned: false,
        availableFilters: ['7d', '30d'],
        currentFilter: '7d',
        chartScheme: {
          name: 'witaScheme',
          selectable: true,
          group: ScaleType.Ordinal,
          domain: ['#99d494ff']
        },
        section: 'alerts',
        loading: true,
        dataLoader: (filter, context) => this.analyticsService.getWeeklyAlerts$(filter, context),
        data: null,
        isGlobalOnly: false
      },
      {
        id: 'alert_types',
        widgetType: 'pie_chart',
        label: 'Tipologia Allerte',
        visible: true,
        cols: 6, rows: 3, pinned: false,
        availableFilters: ['24h', '7d', '30d', '1y'],
        currentFilter: '24h',
        chartScheme: {
          name: 'alertTypeScheme',
          selectable: true,
          group: ScaleType.Ordinal,
          domain: ['#ffca28', '#ef5350', '#42a5f5', '#bdbdbd', '#99d494ff', '#7e2ddbff']
        },
        section: 'alerts',
        showLegend: true,
        loading: true,
        dataLoader: (filter, context) => this.analyticsService.getAlertTypes$(filter, context),
        data: null,
        isGlobalOnly: false
      },
      {
        id: 'chart_hierarchy',
        widgetType: 'sunburst',
        label: 'Analisi Zone Critiche',
        // icon: 'business-outline',
        visible: true,
        cols: 8, rows: 4, // Deve essere bello grande
        pinned: false,
        section: 'alerts',
        loading: true,
        availableFilters: ['24h', '7d', '30d', '1y'],
        currentFilter: '24h',
        dataLoader: (filter, context) => this.analyticsService.getHierarchyStats$(filter, context),
        data: null,
        isGlobalOnly: false
      },
      // --- GRAFICI GRANDI ---
      {
        id: 'heatmap_hourly',
        widgetType: 'heat_map',
        label: 'Distribuzione Oraria',
        visible: true,
        chartScheme: {
          name: 'alertScheme',
          selectable: true,
          group: ScaleType.Linear,
          domain: ['#ffffff', '#e0f2f1', '#ffca28', '#ef5350']
        },
        cols: 12, rows: 6,
        availableFilters: ['all_alerts', 'managed_alerts', 'unmanaged_alerts'],
        currentFilter: 'all_alerts',
        pinned: false,
        section: 'alerts',
        loading: true,
        dataLoader: (filter, context) => this.analyticsService.getHourlyAlerts$(filter, context),
        data: null,
        isGlobalOnly: false
      }
    ];
    return widgets;
  }
}
